// Import React modules
import React, { useEffect, useState, useRef } from "react";
// Import other modules
import {
  doc,
  collection,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  query,
  orderBy,
  getDocs,
  onSnapshot,
  Timestamp,
  deleteDoc,
  where,
} from "firebase/firestore";
// Import Firebase config
import { auth, db } from "../firebase";
import { styled } from "@mui/material/styles";
import {
  Tabs,
  Tab,
  Box,
  Button,
  TextField,
  Autocomplete,
  Typography,
  InputLabel,
} from "@mui/material";
import RegenerateAudioRecorder from "../utils/RegenerateAudioRecorder";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import AttachmentIcon from "@mui/icons-material/Attachment";
import Alert from "@mui/material/Alert";
import CheckIcon from "@mui/icons-material/Check";
// Import assets
import loadingSpinner from "../assets/icons/loading_spinner.svg";
import xMarkIcon from "../assets/icons/xmark.svg";
import { useEditor } from "@tiptap/react";
import TipTap from "../components/TipTap";
// Import CSS stylesheets
import "../styles/variables.css";
import "../styles/layoutStyles.css";
import "../styles/textStyles.css";
import "../styles/buttonStyles.css";
import axios from "axios";
import ChatWithAiSidebar from "../components/ChatWithAiSidebar";
import Tooltip from "@mui/material/Tooltip";
import { MeddbasePID, MeddbasePV1 } from "../types/meddbase";
import { pushToMeddbase } from "../utils/meddbaseUtils";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import enGB from "date-fns/locale/en-GB";

interface Patient {
  id: string;
  title: string;
  first_name: string;
  last_name: string;
  dob: Date;
  sex: string;
  address_1: string;
  address_2: string;
  address_3: string;
  post_code: string;
  email: string;
  phone: string;
  phone_type: string;
  clinical_info: string;
}

interface Recording {
  id: string;
  name: string;
  timestamp: Timestamp;
  noteType: string;
  displayName: string;
  progress: string;
  summary: string;
  htmlSummary: string;
  transcription: string;
  updatedTranscript: string;
  sessionNotes: string;
  patientId?: string;
  patientName?: string;
  isFavourited?: boolean;
  aiSummary?: string;
  summaryVersions?: Summary[];
  summaryVersionsLength?: number;
  summaryVersionsCurrentIndex?: number;
  clippeOutput?: string;
}

interface Summary {
  id: string;
  timestamp: Timestamp;
  summary: string;
  htmlSummary: string;
  noteType: string;
  instruction: string;
  process_time: number;
  summaryChange: string;
}

interface User {
  id: string;
  account?: string;
  stripeSessionId?: string;
  clinikoApiKey?: string;
  meddbaseUserId?: string;
  isMeddbaseUserIdStored?: boolean;
  cancelled?: string;
  currentPeriodEnd?: string;
  title?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  initials?: string;
  photoUrl?: string;
  dob?: Date;
  sex?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  postcode?: string;
  email?: string;
  phone?: string;
  phoneType?: string;
  occupation?: string;
  clinicName?: string;
  clinicRole?: string;
  clinicAddress1?: string;
  clinicAddress2?: string;
  clinicAddress3?: string;
  clinicPostCode?: string;
  clinicEmail?: string;
  clinicPhone?: string;
  clinicPhoneType?: string;
}

type SortPreference = "none" | "favourited" | "unfavourited";

type ActiveTabsType = {
  [key: string]: number;
};

interface HistoryTabContentProps {
  uid: string;
  clinikoApiKey: string;
  user: any;
  currentUser: User;
  unactionedHl7Messages: Array<{
    pid: MeddbasePID;
    pv1: MeddbasePV1;
    id: string;
    timestamp: Date;
  }>;
  setUnactionedHl7Messages: React.Dispatch<React.SetStateAction<any>>;
  meddbasePID: MeddbasePID | null;
  setMeddbasePID: React.Dispatch<React.SetStateAction<MeddbasePID | null>>;
  meddbasePV1: MeddbasePV1 | null;
}

interface StyledTabsProps {
  children?: React.ReactNode;
  value: number;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
}

interface StyledTabProps {
  label: string;
}

interface Template {
  id: string;
  displayName: string;
  description: string;
  visiblePrompt: string;
  hiddenPrompt: string;
  createdAt?: Date | null;
}

interface Templates {
  clinicalNotes: Template[];
  letters: Template[];
  meetingNotes: Template[];
}

const StyledTabs = styled((props: StyledTabsProps) => (
  <Tabs
    {...props}
    TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
  />
))({
  "& .MuiTabs-indicator": {
    display: "flex",
    justifyContent: "center",
    backgroundColor: "transparent",
  },
  "& .MuiTabs-indicatorSpan": {
    width: "90%",
    backgroundColor: "var(--color-brand-green)",
  },
});

const StyledTab = styled((props: StyledTabProps) => (
  <Tab disableRipple {...props} />
))(({ theme }) => ({
  color: "rgba(0, 0, 0, 0.85)",
  "&.Mui-selected": {
    color: "var(--color-brand-green)",
  },
  "&.Mui-focusVisible": {
    backgroundColor: "rgba(100, 95, 228, 0.32)",
  },
}));

const HistoryTabContent: React.FC<HistoryTabContentProps> = ({
  uid,
  clinikoApiKey,
  user,
  currentUser,
  unactionedHl7Messages,
  setUnactionedHl7Messages,
  meddbasePID,
  setMeddbasePID,
  meddbasePV1,
}) => {
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const previousRecordings = useRef<Recording[]>([]);
  const previousSummaryVersionsLength = useRef<number>(0);
  const [searchValue, setSearchValue] = useState("");
  const [expandedRecording, setExpandedRecording] = useState<number | null>(
    null
  );
  const [summary, setSummary] = useState<string>("");
  const [copied, setCopied] = useState<boolean>(false);
  const [isSidebarVisible, setSidebarVisible] = useState(false);
  const [initialMessage, setInitialMessage] = useState<string>("");
  const [chatInput, setChatInput] = useState<string>(""); // State to hold the preformatted chat input

  const [currentNoteDateTime, setCurrentNoteDateTime] = useState({
    date: "",
    time: "",
  });
  const [summaryToReformat, setSummaryToReformat] = useState("");
  const [transcriptionToReformat, setTranscriptionToReformat] = useState("");
  const [patients, setPatients] = useState<Patient[]>([]);
  const [cloudFunctionPatients, setCloudFunctionPatients] = useState<Patient[]>(
    []
  );
  const [isCloudFunctionPatient, setIsCloudFunctionPatient] =
    useState<boolean>(false);
  const [pushToCliniko, setPushToCliniko] = useState<boolean>(false);
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [selectedPatientChanged, setSelectedPatientChanged] = useState(false);
  const [selectedNoteType, setSelectedNoteType] = useState("");
  const [activeTabs, setActiveTabs] = useState<ActiveTabsType>({});
  const [openRegenerateDialog, setOpenRegenerateDialog] = useState(false);
  const [regenerateNoteType, setRegenerateNoteType] = useState("");
  const [regenerateInstruction, setRegenerateInstruction] = useState("");
  const [regenerateSelectedCategory, setRegenerateSelectedCategory] = useState<
    keyof Templates | ""
  >("clinicalNotes");
  const [regenerateSelectedTemplate, setRegenerateSelectedTemplate] =
    useState<string>("");
  const [regenerateIsPreset, setRegenerateIsPreset] = useState<boolean>(true);
  const [regenerateSelectedDisplayName, setRegenerateSelectedDisplayName] =
    useState<string>("");
  const [isFocused, setIsFocused] = useState(false);
  const [presetTemplates, setPresetTemplates] = useState<Templates>({
    clinicalNotes: [],
    letters: [],
    meetingNotes: [],
  });
  const [userTemplates, setUserTemplates] = useState<Templates>({
    clinicalNotes: [],
    letters: [],
    meetingNotes: [],
  });
  const [combinedTemplates, setCombinedTemplates] = useState<Templates>({
    clinicalNotes: [],
    letters: [],
    meetingNotes: [],
  });
  const [pageLoading, setPageLoading] = useState(true);
  const [showRegenerateSuccessfulAlert, setShowRegenerateSuccessfulAlert] =
    useState(false);
  const [loadingRegenerateSummary, setLoadingRegenerateSummary] =
    useState(false);
  const [showAssignToPatientModal, setShowAssignToPatientModal] = useState<{
    isVisible: boolean;
    recordingId: string;
    noteType: string;
    summary: string;
    htmlSummary: string;
    isAssigned: boolean;
    patientName: string;
    assignedPatient: Patient | null | undefined; // Update the type here
  }>({
    isVisible: false,
    recordingId: "",
    noteType: "",
    summary: "",
    htmlSummary: "",
    isAssigned: false,
    patientName: "",
    assignedPatient: null,
  });
  const [isDownloadingDocument, setIsDownloadingDocument] = useState(false);
  const [isPatientInfoCorrect, setIsPatientInfoCorrect] = useState(false);
  const [isMeddbasePatientInfoCorrect, setIsMeddbasePatientInfoCorrect] =
    useState(false);
  const [selectedAssignPatient, setSelectedAssignPatient] =
    useState<Patient | null>(null);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [filteredSearchPatients, setFilteredSearchPatients] = useState<
    Patient[]
  >([]);
  const [assignNoteLocation, setAssignNoteLocation] =
    useState("treatmentNotes");
  const [isAssigningToPatient, setIsAssigningToPatient] = useState(false);
  const [searchFirstName, setSearchFirstName] = useState("");
  const [searchLastName, setSearchLastName] = useState("");
  const [loadingSearchPatientResults, setLoadingSearchPatientResults] =
    useState(false);
  const [searchPatientResults, setSearchPatientResults] = useState<Patient[]>(
    []
  );
  const [toRegenerateRecId, setToRegenerateRecId] = useState("");
  const [toRegenerateRecSummary, setToRegenerateRecSummary] = useState("");
  const [toRegenerateRecTimeStamp, setToRegenerateRecTimeStamp] =
    useState<Date | null>(null);
  const [toRegenerateIndex, setToRegenerateIndex] = useState(0);
  const [selectedPatientIndex, setSelectedPatientIndex] = useState<
    string | undefined
  >(undefined);
  const [searchDone, setSearchDone] = useState(false);
  const [sortFavourited, setSortFavourited] = useState<SortPreference>("none");
  const [loadingAnalyseWithClippe, setLoadingAnalyseWithClippe] =
    useState(false);
  const [audioUrl, setAudioUrl] = useState<string>("");
  const [fullFilename, setFullFilename] = useState<string>("");
  const [summaryProgressText, setSummaryProgressText] =
    useState<string>("Begin processing");
  const [summaryAvailable, setSummaryAvailable] = useState<boolean>(false);
  const [currentRecording, setCurrentRecording] = useState<Recording[]>([]);
  const [recording, setRecording] = useState(false);
  const [sessionNotesUpload, setSessionNotesUpload] = useState<string>("");
  const [newTranscript, setNewTranscript] = useState<string>("");
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isPushingToMeddbase, setIsPushingToMeddbase] = useState(false);
  const [showPushToMeddbaseModal, setShowPushToMeddbaseModal] = useState({
    isVisible: false,
    recordingId: "",
    noteType: "",
    summary: "",
  });
  const [meddbaseFormData, setMeddbaseFormData] = useState<{
    clientID: string;
    familyName: string;
    givenName: string;
    prefix: string;
    dateTimeOfBirth: Date | null;
    administrativeSex: string;
  }>({
    clientID: "",
    familyName: "",
    givenName: "",
    prefix: "",
    dateTimeOfBirth: null,
    administrativeSex: "",
  });
  const [selectedMessageId, setSelectedMessageId] = useState("");
  const [isRemoveFromQueueAfterPush, setIsRemoveFromQueueAfterPush] =
    useState(false);

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = event.target;
    setMeddbaseFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleDateChange = (newValue: Date | null) => {
    setMeddbaseFormData((prevData) => ({
      ...prevData,
      dateTimeOfBirth: newValue,
    }));
  };

  useEffect(() => {
    if (selectedMessageId) {
      const selectedMessage = unactionedHl7Messages.find(
        (msg) => msg.id === selectedMessageId
      );
      if (selectedMessage) {
        setMeddbasePID({
          ...selectedMessage.pid,
          id: selectedMessage.id,
          timestamp: selectedMessage.timestamp,
        });
      }
    }
  }, [selectedMessageId, unactionedHl7Messages, setMeddbasePID]);

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setMeddbaseFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Here you can process the form data and create the HL7 message
    console.log(meddbaseFormData);
    // TODO: Call the function to push data to Meddbase
  };

  const toggleSidebar = () => {
    // Toggle the visibility of the sidebar
    setSidebarVisible(!isSidebarVisible);

    // If we are closing the sidebar, reset the states
    if (isSidebarVisible) {
      // Reset the summary and transcription content
      setSummaryToReformat("");
      setTranscriptionToReformat("");
      // Reset the initial chat message
      setInitialMessage("");
      // Optionally, reset any other states as needed here
    }
  };

  // Only show options if 3 or more characters have been entered
  useEffect(() => {
    if (searchInputValue.length >= 3) {
      setFilteredSearchPatients(
        patients.filter((patient) =>
          `${patient.first_name} ${patient.last_name}`
            .toLowerCase()
            .includes(searchInputValue.toLowerCase())
        )
      );
    } else {
      setFilteredSearchPatients([]);
    }
  }, [searchInputValue, patients]);

  const processSummary = (
    noteType: string | undefined | null,
    rawSummary: string | undefined | null
  ): string => {
    if (!rawSummary) {
      return "";
    }

    let htmlContent = "";

    if (!rawSummary) {
      return htmlContent;
    } else if (!/<h1>.+?<\/h1>/.test(rawSummary)) {
      if (noteType) {
        htmlContent += `<h1>${noteType}</h1><br>`;
      }
    }
    let rawSummaryCopy = rawSummary;
    const lines = rawSummaryCopy.split(/(?<=\n)/);
    lines.forEach((line) => {
      // const trimmedLine = line.trim();
      const trimmedLine = line.trim();
      const fullLine = line;
      let temp = trimmedLine;

      if (trimmedLine.startsWith("##") && trimmedLine.endsWith("##")) {
        temp = `<h2>${trimmedLine.slice(2, -2)}</h2>`;
      } else if (trimmedLine.startsWith("**") && trimmedLine.endsWith("**")) {
        temp = `<strong>${trimmedLine.slice(2, -2)}</strong>`;
      } else if (trimmedLine.endsWith(":")) {
        temp = `<strong>${trimmedLine}</strong>`;
      } else {
        temp = fullLine
          .replace(/(\d+\.\s*)?\*\*(.+?)\*\*/g, "<strong>$1$2</strong>")
          .replace(
            /^[ \t]{1,2}- (.+?)(?=\n|$)/gm,
            "<p>&nbsp;&nbsp;&nbsp;&nbsp;- $1</p>"
          )
          .replace(/^- (.+?)(?=\n|$)/gm, "<p>- $1</p>")
          .replace(/^\s*-(.+?):/gm, "<strong>- $1:</strong>");
      }

      if (!temp.match(/<\/?[a-z][\s\S]*>/i)) {
        temp = `<p>${temp}</p>`;
      }

      // else if (temp === "\n") {
      //   temp = "<br />";
      // }

      htmlContent += temp;
    });

    // htmlContent = htmlContent.replace(/<\/p>\s*<br\s*\/?>\s*<p>/g, "</p><p>");

    return htmlContent || rawSummary;
  };

  useEffect(() => {
    let unsubscribeFromRecordings = () => {};
    if (currentUser) {
      const userDocRef = doc(db, "users", currentUser.id);
      const recordingsCollectionRef = collection(userDocRef, "recordings");

      // Fetch all existing recordings initially
      const fetchInitialRecordings = async () => {
        setPageLoading(true);
        try {
          const querySnapshot = await getDocs(
            query(recordingsCollectionRef, orderBy("timestamp", "desc"))
          );
          const initialRecordings = await Promise.all(
            querySnapshot.docs.map(async (docSnapshot) => {
              try {
                const data = docSnapshot.data() as Recording;
                const id = docSnapshot.id;

                const summaryVersionsRef = collection(
                  db,
                  `users/${currentUser.id}/recordings/${id}/summaryVersions`
                );
                const summaryVersionsSnapshot = await getDocs(
                  summaryVersionsRef
                );
                const summaryVersions = (
                  await Promise.all(
                    summaryVersionsSnapshot.docs.map(async (doc) => {
                      const processedSummary = await processSummary(
                        doc.data().displayName || doc.data().noteType || "",
                        doc.data().summary
                      );
                      return {
                        id: doc.id,
                        timestamp: doc.data().timestamp,
                        summary: processedSummary,
                        htmlSummary: doc.data().summary,
                        ...doc.data(),
                      };
                    })
                  )
                ).sort(
                  (a: any, b: any) => a.timestamp - b.timestamp
                ) as Summary[];

                const processedSummary = await processSummary(
                  data.displayName || data.noteType || "",
                  data.summary
                );
                return {
                  ...data,
                  id,
                  name: data.name || "No name",
                  updatedTranscript: data.updatedTranscript || "",
                  isFavourited: data.isFavourited,
                  summary: processedSummary,
                  htmlSummary: data.summary,
                  summaryVersions,
                  summaryVersionsLength: summaryVersions.length,
                  summaryVersionsCurrentIndex: summaryVersions.length - 1,
                  clippeOutput: data.clippeOutput,
                };
              } catch (error) {
                console.error(
                  `Error processing recording ${docSnapshot.id}:`,
                  error
                );
                return null;
              }
            })
          );
          setRecordings(
            initialRecordings.filter((recording) => recording !== null)
          );
        } catch (error) {
          console.error("Error fetching initial recordings:", error);
          setRecordings([]);
          setPageLoading(false);
        } finally {
          setPageLoading(false);
        }
      };

      // Call the initial fetch function
      fetchInitialRecordings();

      // Set up a listener for future updates
      unsubscribeFromRecordings = onSnapshot(
        query(recordingsCollectionRef, orderBy("timestamp", "desc")),
        async (snapshot) => {
          snapshot.docChanges().forEach(async (change) => {
            if (change.type === "modified" || change.type === "added") {
              const docId = change.doc.id;
              const docData = change.doc.data() as Recording;

              // Fetch updated summary versions
              const summaryVersionsRef = collection(
                db,
                `users/${currentUser.id}/recordings/${docId}/summaryVersions`
              );
              const summaryVersionsSnapshot = await getDocs(summaryVersionsRef);
              const summaryVersions = (
                await Promise.all(
                  summaryVersionsSnapshot.docs.map(async (doc) => {
                    const processedSummary = await processSummary(
                      doc.data().displayName || doc.data().noteType || "",
                      doc.data().summary
                    );
                    return {
                      id: doc.id,
                      timestamp: doc.data().timestamp,
                      summary: processedSummary,
                      htmlSummary: doc.data().summary,
                      ...doc.data(),
                    };
                  })
                )
              ).sort(
                (a: any, b: any) => a.timestamp - b.timestamp
              ) as Summary[];

              const processedSummary = await processSummary(
                docData.displayName || docData.noteType || "",
                docData.summary
              );

              // Update state with the modified/added recording
              setRecordings((prevRecordings) => {
                const existingIndex = prevRecordings.findIndex(
                  (rec) => rec.id === docId
                );

                if (existingIndex !== -1) {
                  // Update existing recording
                  return prevRecordings.map((rec, index) =>
                    index === existingIndex
                      ? {
                          ...docData,
                          id: docId,
                          name: docData.name || "No name",
                          isFavourited: docData.isFavourited,
                          summary: processedSummary,
                          htmlSummary: docData.summary,
                          summaryVersions,
                          summaryVersionsLength: summaryVersions.length,
                          summaryVersionsCurrentIndex:
                            previousRecordings.current[index] &&
                            previousRecordings.current[index]
                              .summaryVersionsLength === summaryVersions.length
                              ? previousRecordings.current[index]
                                  .summaryVersionsCurrentIndex
                              : summaryVersions.length - 1,
                          clippeOutput: docData.clippeOutput,
                        }
                      : rec
                  );
                } else {
                  // Add new recording
                  return [
                    ...prevRecordings,
                    {
                      ...docData,
                      id: docId,
                      name: docData.name || "No name",
                      summary: processedSummary,
                      summaryVersions,
                      summaryVersionsLength: summaryVersions.length,
                      summaryVersionsCurrentIndex: summaryVersions.length - 1,
                      clippeOutput: docData.clippeOutput,
                    },
                  ];
                }
              });
            }
          });
        }
      );
    }

    return () => {
      if (unsubscribeFromRecordings) {
        unsubscribeFromRecordings();
      }
    };
  }, [uid]);

  useEffect(() => {
    previousRecordings.current = recordings;
  }, [recordings]);

  useEffect(() => {
    setIsCloudFunctionPatient(
      !!cloudFunctionPatients.find(
        (patient) => patient.id === selectedPatientId
      )
    );
  }, [cloudFunctionPatients, selectedPatientId]);

  const handleRecordingClick = () => {
    if (regenerateSelectedTemplate !== "") {
      setRecording((prevState) => !prevState);
    }
  };

  const handleAudioUrl = (audioUrl: string, fullFilename: string) => {
    setAudioUrl(audioUrl);
    setFullFilename(`audioFiles/${fullFilename}`);
    setSummaryAvailable(false);
    setCurrentRecording([]);
    // console.log("Audio URL in handleAudioUrl:", audioUrl);
    // console.log("Filename in handleAudioUrl:", fullFilename);
  };

  // const fetchPatients = async () => {
  //   try {
  //     // Fetch patients from the cloud function
  //     const cloudFunctionResponse = await axios.post(
  //       "https://europe-west2-motics-consultations.cloudfunctions.net/cliniko-fetch-patients-staging",
  //       { uid }
  //     );

  //     const cloudFunctionPatients: Patient[] = (
  //       cloudFunctionResponse.data.patients || []
  //     ).map((patient: any) => ({
  //       ...patient,
  //       dob: new Date(patient.date_of_birth),
  //       phone:
  //         patient.patient_phone_numbers.length > 0
  //           ? patient.patient_phone_numbers[0].normalized_number
  //           : "",
  //       phone_type:
  //         patient.patient_phone_numbers.length > 0
  //           ? patient.patient_phone_numbers[0].phone_type
  //           : "",
  //     }));

  //     setCloudFunctionPatients(cloudFunctionPatients);
  //     // console.log("Total cloud patients: ", cloudFunctionPatients.length);

  //     // Fetch patients from the patient collection in Firebase
  //     const userRef = doc(db, "users", uid);
  //     const userDoc = await getDoc(userRef);
  //     if (userDoc.exists()) {
  //       const patientsCollectionRef = collection(userRef, "patients");
  //       const patientsSnapshot = await getDocs(patientsCollectionRef);
  //       const firebasePatients: Patient[] = patientsSnapshot.docs.map(
  //         (doc) => ({
  //           id: doc.id,
  //           ...doc.data(),
  //         })
  //       ) as Patient[];

  //       // Merge the patients from both sources
  //       const mergedPatients = [...cloudFunctionPatients, ...firebasePatients];
  //       setPatients(mergedPatients);
  //       // console.log("Total patients: ", mergedPatients.length);
  //       // console.log("Patients array:", patients);
  //     } else {
  //       console.error("User document does not exist");
  //     }
  //   } catch (error) {
  //     console.error("Error fetching patients from cloud function:", error);
  //     // Fetch patients from the patient collection in Firebase only
  //     const userRef = doc(db, "users", uid);
  //     const patientsCollectionRef = collection(userRef, "patients");
  //     const patientsSnapshot = await getDocs(patientsCollectionRef);
  //     const firebasePatients: Patient[] = patientsSnapshot.docs.map((doc) => ({
  //       id: doc.id,
  //       ...doc.data(),
  //     })) as Patient[];
  //     setPatients(firebasePatients);
  //     // console.log("Patients array (Firebase only):", firebasePatients);
  //   }
  // };

  // useEffect(() => {
  //   if (uid) {
  //     fetchPatients();
  //   }
  // }, [uid]);

  useEffect(() => {
    const fetchTemplates = async () => {
      setPageLoading(true);
      const templateCategories = ["clinicalNotes", "letters", "meetingNotes"];
      let fetchedPresetTemplates: Templates = {
        clinicalNotes: [],
        letters: [],
        meetingNotes: [],
      };
      let fetchedUserTemplates: Templates = {
        clinicalNotes: [],
        letters: [],
        meetingNotes: [],
      };

      const fetchCategoryTemplates = async (
        docRef: any,
        category: keyof Templates,
        templates: Templates
      ) => {
        const categoryCollectionRef = collection(docRef, category);
        const categoryQuerySnapshot = await getDocs(categoryCollectionRef);

        const fetchedTemplates: Template[] = [];

        categoryQuerySnapshot.forEach((docSnapshot) => {
          if (docSnapshot.exists()) {
            const typeData = docSnapshot.data();
            const template: Template = {
              id: docSnapshot.id,
              displayName: typeData.displayName || "",
              description: typeData.description || "",
              visiblePrompt: typeData.visiblePrompt || "",
              hiddenPrompt: typeData.hiddenPrompt || "",
              createdAt: typeData.createdAt
                ? new Date(typeData.createdAt)
                : null,
            };

            fetchedTemplates.push(template);
          }
        });

        // Sort the fetched templates by createdAt if it exists
        fetchedTemplates.sort((a, b) => {
          const aCreatedAt = a.createdAt ? a.createdAt.getTime() : 0;
          const bCreatedAt = b.createdAt ? b.createdAt.getTime() : 0;
          return aCreatedAt - bCreatedAt;
        });

        templates[category] = fetchedTemplates;
      };

      // Fetching preset and user templates
      if (uid) {
        for (const category of templateCategories) {
          await Promise.all([
            fetchCategoryTemplates(
              doc(db, "moticsTemplates", "current"),
              category as keyof Templates,
              fetchedPresetTemplates
            ),
            fetchCategoryTemplates(
              doc(db, "users", uid, "templates", "current"),
              category as keyof Templates,
              fetchedUserTemplates
            ),
          ]);
        }
      }

      // Merging fetchedPresetTemplates and fetchedUserTemplates
      const combinedTemplates: Templates = templateCategories.reduce(
        (acc, category) => {
          acc[category as keyof Templates] = [
            ...(fetchedPresetTemplates[category as keyof Templates] || []),
            ...(fetchedUserTemplates[category as keyof Templates] || []),
          ];
          return acc;
        },
        {} as Templates
      );

      setPresetTemplates(fetchedPresetTemplates);
      setUserTemplates(fetchedUserTemplates);
      setCombinedTemplates(combinedTemplates);
      setPageLoading(false);
    };

    fetchTemplates();
  }, [uid]);

  const updateContentOnFirebase = async (
    recordingId: string,
    summaryVersionId: string,
    newContent: string,
    type: string
  ) => {
    if (newContent.trim() === "") {
      // console.log("Content is empty. Not saving to Firebase.");
      return;
    }

    try {
      // console.log(`Updating ${type} on Firebase... Content: `, newContent);
      const recordingDocRef = doc(db, `users/${uid}/recordings`, recordingId);

      // Update the document on Firebase
      const updateData = { [type]: newContent };
      await updateDoc(recordingDocRef, updateData);

      // Update the specific summary version if needed
      if (summaryVersionId !== "") {
        const regeneratedSummaryDocRef = doc(
          db,
          `users/${uid}/recordings/${recordingId}/summaryVersions`,
          summaryVersionId
        );
        await updateDoc(regeneratedSummaryDocRef, { summary: newContent });
      }

      // Update the local state
      setRecordings((prevRecordings) => {
        return prevRecordings.map((recording) => {
          if (recording.id === recordingId) {
            // Update the specific field in the recording
            const updatedRecording = { ...recording, [type]: newContent };

            if (
              type === "summary" &&
              summaryVersionId &&
              recording.summaryVersions
            ) {
              updatedRecording.summaryVersions = recording.summaryVersions.map(
                (summary) => {
                  if (summary.id === summaryVersionId) {
                    return { ...summary, summary: newContent };
                  }
                  return summary;
                }
              );
            }
            return updatedRecording;
          }
          return recording;
        });
      });
    } catch (error) {
      console.error("Error updating content on Firebase:", error);
    }
  };

  const handleTabChange = (recordingId: string, newValue: any) => {
    setActiveTabs((prevTabs) => ({ ...prevTabs, [recordingId]: newValue }));
  };

  const toggleExpand = (index: number, recordingId: string) => {
    if (expandedRecording === index) {
      setExpandedRecording(null);
      // Reset the active tab to default (Summary) when collapsing
      setActiveTabs((prevTabs) => ({ ...prevTabs, [recordingId]: 0 }));
    } else {
      setExpandedRecording(index);
      // Set the active tab to whatever it was before or default to Summary
      setActiveTabs((prevTabs) => ({
        ...prevTabs,
        [recordingId]: prevTabs[recordingId] || 0,
      }));
    }
  };

  const handleOpenRegenerateDialog = (
    recId: string,
    recSummary: string,
    recTimeStamp: any,
    index: number
  ) => {
    setToRegenerateRecId(recId);
    setToRegenerateRecSummary(recSummary);
    setToRegenerateRecTimeStamp(recTimeStamp);
    setToRegenerateIndex(index);
    setOpenRegenerateDialog(true);
  };

  const handleCloseRegenerateDialog = () => {
    setNewTranscript("");
    setOpenRegenerateDialog(false);
  };

  const handleAnalyseWithClippe = async (recId: string, recSummary: string) => {
    try {
      const userId = auth.currentUser ? auth.currentUser.uid : null;
      if (!userId) {
        console.error("No authenticated user found.");
        return;
      }

      setLoadingAnalyseWithClippe(true);

      const response = await fetch(
        "https://europe-west2-motics-consultations.cloudfunctions.net/generate-clippe-output",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ note: recSummary }),
        }
      );
      // console.log("CLIPPE response: ", response);
      const data = await response.json();
      // console.log("CLIPPE output: ", data.output);
      const clippeOutput = data.output;

      if (clippeOutput) {
        const recordingRef = doc(db, `users/${userId}/recordings/${recId}`);
        await updateDoc(recordingRef, { clippeOutput: clippeOutput });
        setRecordings((prevRecordings) =>
          prevRecordings.map((recording) => {
            if (recording.id === recId) {
              return {
                ...recording,
                clippeOutput: clippeOutput,
              };
            }
            return recording;
          })
        );
      } else {
        console.error("Data is missing from the response");
      }
    } catch (error) {
      console.error("Error analysing with CLIPPE:", error);
      alert("Failed to analyse with CLIPPE. Please try again.");
    } finally {
      setLoadingAnalyseWithClippe(false);
    }
  };

  const handlePushToMeddbase = async () => {
    setIsPushingToMeddbase(true);

    try {
      let pushResult;
      let updateData: any = {};
      let formattedData: MeddbasePID;

      if (isMeddbasePatientInfoCorrect && meddbasePID) {
        // Use existing meddbasePID information
        pushResult = await pushToMeddbase(
          processSummary(null, showPushToMeddbaseModal.summary),
          meddbasePID,
          showPushToMeddbaseModal.noteType
        );
        // Prepare update data
        if (meddbasePID.patientID?.idNumber) {
          updateData.patientId = meddbasePID.patientID.idNumber;
        }
        if (
          meddbasePID.patientName?.givenName &&
          meddbasePID.patientName?.familyName
        ) {
          updateData.patientName = `${meddbasePID.patientName.givenName} ${meddbasePID.patientName.familyName}`;
        }
        if (meddbasePID.dateTimeOfBirth) {
          updateData.patientDOB = meddbasePID.dateTimeOfBirth;
        }
        if (meddbasePID.administrativeSex) {
          updateData.patientSex = meddbasePID.administrativeSex;
        }
      } else {
        // Use form data to create HL7 message
        const requiredFields = [
          "clientID",
          "familyName",
          "givenName",
          "dateTimeOfBirth",
        ];

        const missingFields = requiredFields.filter(
          (field) => !meddbaseFormData[field as keyof typeof meddbaseFormData]
        );

        if (missingFields.length > 0) {
          // Alert the user about missing fields but don't close the modal
          alert(
            `Please fill in the following required fields: ${missingFields.join(
              ", "
            )}`
          );
          setIsPushingToMeddbase(false);
          return; // Exit the function early, allowing the user to continue typing
        }

        // If all required fields are filled, proceed with pushing to Meddbase
        formattedData = {
          id: "1",
          timestamp: new Date(),
          setID: "1",
          patientID: { idNumber: meddbaseFormData.clientID },
          patientIdentifierList: {
            idNumber: meddbaseFormData.clientID,
            identifierTypeCode: "",
          },
          patientName: {
            familyName: meddbaseFormData.familyName,
            givenName: meddbaseFormData.givenName,
            middleName: "",
            suffix: "",
            degree: "",
            prefix: meddbaseFormData.prefix || "",
            nameTypeCode: "",
          },
          dateTimeOfBirth: {
            time: meddbaseFormData.dateTimeOfBirth?.toISOString() || "",
          },
          administrativeSex: meddbaseFormData.administrativeSex,
          patientAddress: {
            streetAddress: "",
            otherDesignation: "",
            city: "",
            zipOrPostalCode: "",
            country: "",
            addressType: "",
          },
          phoneNumberHome: {
            telephoneNumber: "",
            telecommunicationUseCode: "",
            telecommunicationEquipmentTypeCode: "",
            emailAddress: "",
          },
          phoneNumberBusiness: {
            telephoneNumber: "",
            telecommunicationUseCode: "",
            telecommunicationEquipmentTypeCode: "",
            emailAddress: "",
          },
          countyCode: "",
        };

        pushResult = await pushToMeddbase(
          processSummary(null, showPushToMeddbaseModal.summary),
          formattedData,
          showPushToMeddbaseModal.noteType
        );

        updateData = {
          patientId: meddbaseFormData.clientID,
          patientName: `${meddbaseFormData.givenName} ${meddbaseFormData.familyName}`,
          patientPrefix: meddbaseFormData.prefix || "",
          patientDOB: meddbaseFormData.dateTimeOfBirth,
          patientSex: meddbaseFormData.administrativeSex,
        };
      }

      if (pushResult && pushResult.success && pushResult.hl7Message) {
        updateData.isPushedToMeddbase = true;
        updateData.pushedSummary = showPushToMeddbaseModal.summary;
        updateData.meddbaseResponse = pushResult.meddbaseResponse;
        updateData.pushedHl7Message = pushResult.hl7Message;
        updateData.pushedToMeddbaseAt = new Date();

        const recordingDocRef = doc(
          db,
          `users/${uid}/recordings/${showPushToMeddbaseModal.recordingId}`
        );

        await updateDoc(recordingDocRef, updateData);

        const patientName = updateData.patientName || "Unknown";

        alert(`Summary pushed to ${patientName}'s Meddbase record.`);

        // Close the modal only after successful push
        setShowPushToMeddbaseModal({
          isVisible: false,
          recordingId: "",
          noteType: "",
          summary: "",
        });
      } else {
        updateData.pushedToMeddbaseAt = new Date();
        updateData.pushedHl7Message = pushResult.hl7Message;
        updateData.meddbaseResponse = pushResult.meddbaseResponse;
        updateData.pushToMeddbaseError = pushResult.error;

        const recordingDocRef = doc(
          db,
          `users/${uid}/recordings/${showPushToMeddbaseModal.recordingId}`
        );

        await updateDoc(recordingDocRef, updateData);
        throw new Error("Failed to push to Meddbase");
      }
    } catch (error) {
      console.error("Error pushing to Meddbase:", error);
      if (
        error instanceof Error &&
        error.message !== "Failed to push to Meddbase"
      ) {
        alert("Failed to push summary to Meddbase. Please try again.");
      }
    } finally {
      if (isRemoveFromQueueAfterPush) {
        try {
          if (currentUser?.meddbaseUserId && meddbasePID?.id) {
            const messageRef = doc(
              db,
              "meddbase",
              "psm",
              "users",
              currentUser.meddbaseUserId,
              "messages",
              meddbasePID.id
            );

            await updateDoc(messageRef, { actioned: true });

            // Remove the message from unactionedHl7Messages
            try {
              setUnactionedHl7Messages((prevMessages: any) =>
                prevMessages.filter(
                  (message: any) => message.id !== meddbasePID.id
                )
              );
            } catch (error) {
              console.error(
                "Error updating actioned hl7 message status:",
                error
              );
            } finally {
              const latestMessage =
                unactionedHl7Messages
                  .filter((message) => message.id !== meddbasePID.id)
                  .slice(-1)[0] || null;

              if (latestMessage) {
                setMeddbasePID({
                  ...latestMessage.pid,
                  id: latestMessage.id,
                  timestamp: latestMessage.timestamp,
                });
                setSelectedMessageId(latestMessage.id);
              } else {
                setMeddbasePID(null);
              }
            }
          }
        } catch (error) {
          console.error("Error updating message status:", error);
        }
      }
      setIsPushingToMeddbase(false);
    }
  };

  const formatTimestamp = (
    timestamp: Date | { seconds: number; nanoseconds: number }
  ) => {
    const date =
      timestamp instanceof Date
        ? timestamp
        : new Date(timestamp.seconds * 1000);
    return date
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(",", "");
  };

  const renderPushToMeddbaseModal = () => {
    if (!showPushToMeddbaseModal.isVisible) return null;

    const formatDateOfBirth = (dateString: string): string => {
      if (!dateString || dateString.length !== 8) return "Invalid Date";

      const year = dateString.slice(0, 4);
      const month = dateString.slice(4, 6);
      const day = dateString.slice(6, 8);

      const date = new Date(`${year}-${month}-${day}`);
      return format(date, "dd/MM/yyyy");
    };

    return (
      <div className="box-sizing-default modal-background">
        <div
          className="box-sizing-default modals"
          id="regenerate-summary-modal"
          style={{ padding: "0" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
              padding: "1rem 2rem 0 2rem",
            }}
          >
            <div style={{ fontSize: "2rem", fontWeight: "500" }}>
              Push to Meddbase
            </div>
            <button
              className="small-icon-button"
              style={{ alignSelf: "center" }}
              onClick={() =>
                setShowPushToMeddbaseModal({
                  isVisible: false,
                  recordingId: "",
                  noteType: "",
                  summary: "",
                })
              }
            >
              <img src={xMarkIcon} alt="close" />
            </button>
          </div>

          <div style={{ overflow: "auto", padding: "1rem 2rem" }}>
            {meddbasePID && (
              <div>
                {unactionedHl7Messages.length > 0 && (
                  <div>
                    <select
                      style={{
                        width: "100%",
                        marginBottom: "1rem",
                        padding: "0.2rem 0.5rem",
                      }}
                      value={selectedMessageId || meddbasePID.id}
                      onChange={(e) => setSelectedMessageId(e.target.value)}
                    >
                      <option value="">Select a patient</option>
                      {unactionedHl7Messages.map((message) => (
                        <option key={message.id} value={message.id}>
                          {`${formatTimestamp(message.timestamp)} - ${
                            message.pid.patientName.givenName
                          } ${message.pid.patientName.familyName} (${
                            message.pid.patientID.idNumber
                          })`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <p>
                  Are you sure you want to push this summary to the following
                  patient record?
                </p>
                <div style={{ marginBottom: "2rem" }}>
                  <div
                    style={{
                      backgroundColor: "var(--color-brand-dark-green)",
                      color: "white",
                      padding: "1rem",
                      borderRadius: "0.5rem",
                    }}
                  >
                    <p
                      style={{
                        color: "white",
                        fontWeight: "bold",
                        marginBottom: "0.5rem",
                      }}
                    >
                      Client Details
                    </p>
                    <p
                      style={{
                        color: "white",
                        fontSize: "0.8rem",
                        margin: "0.25rem 0",
                      }}
                    >
                      Arrived: {formatTimestamp(meddbasePID.timestamp)}
                    </p>
                    <p
                      style={{
                        color: "white",
                        fontSize: "0.8rem",
                        margin: "0.25rem 0",
                      }}
                    >
                      ID: {meddbasePID.patientID.idNumber}
                    </p>
                    <p
                      style={{
                        color: "white",
                        fontSize: "0.8rem",
                        margin: "0.25rem 0",
                      }}
                    >
                      Name: {meddbasePID.patientName.givenName}{" "}
                      {meddbasePID.patientName.familyName}
                    </p>
                    <p
                      style={{
                        color: "white",
                        fontSize: "0.8rem",
                        margin: "0.25rem 0",
                      }}
                    >
                      Date of Birth:{" "}
                      {formatDateOfBirth(meddbasePID.dateTimeOfBirth.time)}
                    </p>
                    {meddbasePID.patientAddress.streetAddress && (
                      <p
                        style={{
                          color: "white",
                          fontSize: "0.8rem",
                          margin: "0.25rem 0",
                        }}
                      >
                        Address:{" "}
                        <>
                          {meddbasePID.patientAddress.streetAddress}{" "}
                          {meddbasePID.patientAddress.city && ", "}
                        </>
                        {meddbasePID.patientAddress.city && (
                          <>
                            {meddbasePID.patientAddress.city}{" "}
                            {meddbasePID.patientAddress.zipOrPostalCode && ", "}
                          </>
                        )}
                        {meddbasePID.patientAddress.zipOrPostalCode}
                      </p>
                    )}
                    <div
                      className="box-sizing-default alert-text"
                      style={{
                        marginTop: "0.5rem",
                        color: "white",
                        fontStyle: "italic",
                      }}
                    >
                      If this is the wrong client, please arrive the correct
                      client on Meddbase. If the client details are incorrect,
                      please update their details on Meddbase.
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <label>
                      <input
                        type="checkbox"
                        value="isMeddbasePatientInfoCorrect"
                        onChange={(e) =>
                          setIsMeddbasePatientInfoCorrect(e.target.checked)
                        }
                      />
                      Confirm client information is correct and push to
                      Meddbase.
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        value="isRemoveFromQueueAfterPush"
                        onChange={(e) =>
                          setIsRemoveFromQueueAfterPush(e.target.checked)
                        }
                      />
                      Remove patient from queue after push to Meddbase.
                    </label>
                  </div>
                </div>
                <div style={{ textAlign: "center", marginBottom: "2rem" }}>
                  --- OR ---
                </div>
              </div>
            )}
            <div>
              <div>Enter client details manually to push to Meddbase.</div>
              <form onSubmit={(e) => e.preventDefault()}>
                <div style={{ display: "flex", gap: "1rem" }}>
                  <TextField
                    label="Client ID"
                    name="clientID"
                    value={meddbaseFormData.clientID}
                    onChange={handleInputChange}
                    fullWidth
                    size="small"
                    margin="normal"
                    required
                  />
                  <TextField
                    label="Prefix"
                    name="prefix"
                    value={meddbaseFormData.prefix}
                    onChange={handleInputChange}
                    fullWidth
                    size="small"
                    margin="normal"
                  />
                </div>
                <div style={{ display: "flex", gap: "1rem" }}>
                  <TextField
                    label="Given Name"
                    name="givenName"
                    value={meddbaseFormData.givenName}
                    onChange={handleInputChange}
                    fullWidth
                    size="small"
                    margin="normal"
                    required
                  />
                  <TextField
                    label="Family Name"
                    name="familyName"
                    value={meddbaseFormData.familyName}
                    onChange={handleInputChange}
                    fullWidth
                    size="small"
                    margin="normal"
                    required
                  />
                </div>
                <div style={{ display: "flex", gap: "1rem" }}>
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={enGB}
                  >
                    <DatePicker
                      label="Date of Birth"
                      value={meddbaseFormData.dateTimeOfBirth}
                      onChange={(newValue) => handleDateChange(newValue)}
                      format="dd/MM/yyyy"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          margin: "normal",
                          size: "small",
                          required: true,
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <FormControl fullWidth margin="normal" size="small">
                    <InputLabel id="administrative-sex-label">
                      Administrative Sex
                    </InputLabel>
                    <Select
                      labelId="administrative-sex-label"
                      label="Administrative Sex"
                      name="administrativeSex"
                      value={meddbaseFormData.administrativeSex}
                      onChange={handleSelectChange}
                      size="small"
                    >
                      <MenuItem value="M">Male</MenuItem>
                      <MenuItem value="F">Female</MenuItem>
                      <MenuItem value="U">Unspecified</MenuItem>
                    </Select>
                  </FormControl>
                </div>
              </form>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "2rem",
                gap: "1rem",
              }}
            >
              <button
                className="button-link"
                onClick={() =>
                  setShowPushToMeddbaseModal({
                    isVisible: false,
                    recordingId: "",
                    noteType: "",
                    summary: "",
                  })
                }
              >
                Cancel
              </button>
              <button
                className="button-primary"
                style={{ width: "fit-content", fontWeight: "500" }}
                disabled={isPushingToMeddbase}
                onClick={handlePushToMeddbase}
              >
                Confirm Push
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleRegenerateSummary = async (
    recId: string,
    existingSummary: string,
    existingSummaryTimeStamp: Date
  ) => {
    if (!regenerateSelectedTemplate) {
      alert("Please select a template to regenerate the summary.");
      return;
    }
    handleCloseRegenerateDialog();

    const userId = auth.currentUser ? auth.currentUser.uid : null;
    if (!userId) {
      console.error("No authenticated user found.");
      return;
    }

    setLoadingRegenerateSummary(true);

    try {
      const regeneratedSummaryColRef = collection(
        db,
        `users/${userId}/recordings/${recId}/summaryVersions`
      );

      // Check if the original summary exists in the summaryVersions collection
      const regeneratedSummariesSnapshot = await getDocs(
        regeneratedSummaryColRef
      );
      if (regeneratedSummariesSnapshot.empty) {
        await addDoc(regeneratedSummaryColRef, {
          templateID: "",
          templateCategory: regenerateSelectedCategory,
          templateIsPreset: regenerateIsPreset,
          templateDisplayName: regenerateSelectedDisplayName,
          noteType: regenerateSelectedDisplayName,
          summaryChange: "Original",
          instruction: "",
          timestamp: existingSummaryTimeStamp,
          summary: existingSummary,
        });
      }

      const recordingDocRef = doc(db, `users/${userId}/recordings/${recId}`);
      const recordingDoc = await getDoc(recordingDocRef);
      let updatedTranscript = "";
      const practitionerTitle = currentUser.title
        ? currentUser.title
        : "[practitioner title]";
      const practitionerName = `${
        currentUser.firstName
          ? currentUser.firstName
          : "[practitioner's first name]"
      } ${
        currentUser.lastName
          ? currentUser.lastName
          : "[practitioner's last name]"
      }`;
      const practitionerClinic = currentUser.clinicName
        ? currentUser.clinicName
        : "[practitioner's clinic]";
      const practitionerClinicAddress1 = currentUser.clinicAddress1
        ? currentUser.clinicAddress1
        : "[practitioner's clinic address line 1]";
      const practitionerClinicAddress2 = currentUser.clinicAddress2
        ? currentUser.clinicAddress2
        : "[practitioner's clinic address line 2]";
      const practitionerClinicAddress3 = currentUser.clinicAddress3
        ? currentUser.clinicAddress3
        : "[practitioner's clinic address line 3]";
      const practitionerClinicPostCode = currentUser.clinicPostCode
        ? currentUser.clinicPostCode
        : "[practitioner's clinic postcode]";
      const practitionerClinicContactNumber = currentUser.clinicPhone
        ? currentUser.clinicPhone
        : "[practitioner's clinic contact number]";
      let patientName = null;
      let patientId = null;
      let patientDOB = null;
      let patientAddress1 = null;
      let patientAddress2 = null;
      let patientAddress3 = null;
      let patientPostcode = null;
      let patientEmail = null;
      let patientSex = null;
      if (recordingDoc.exists()) {
        const recordingData = recordingDoc.data();
        const existingTranscript =
          updatedTranscript && updatedTranscript.trim() !== ""
            ? updatedTranscript
            : recordingData.transcription || "";

        if (newTranscript && newTranscript.trim() !== "") {
          const today = new Date();
          const formattedDate =
            today
              .toLocaleString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })
              .replace(",", "") + (today.getHours() < 12 ? " AM" : " PM");

          updatedTranscript = `${existingTranscript}\n\n<strong>Transcript Addendum - ${formattedDate}</strong>\n${newTranscript}`;
        } else {
          updatedTranscript = existingTranscript;
        }
        patientName = recordingData.patientName
          ? recordingData.patientName
          : null;
        patientId = recordingData.patientId ? recordingData.patientId : null;
        patientDOB = recordingData.patientDOB ? recordingData.patientDOB : null;
        patientAddress1 = recordingData.patientAddress1
          ? recordingData.patientAddress1
          : null;
        patientAddress2 = recordingData.patientAddress2
          ? recordingData.patientAddress2
          : null;
        patientAddress3 = recordingData.patientAddress3
          ? recordingData.patientAddress3
          : null;
        patientPostcode = recordingData.patientPostcode
          ? recordingData.patientPostcode
          : null;
        patientEmail = recordingData.patientEmail
          ? recordingData.patientEmail
          : null;
        patientSex = recordingData.patientSex ? recordingData.patientSex : null;
        patientName = patientName ? patientName : "[patient's name]";
        patientDOB = patientDOB
          ? formatDate(patientDOB.toDate())
          : "[patient's date of birth]";
        patientAddress1 = patientAddress1
          ? patientAddress1
          : "[patient's address line 1]";
        patientAddress2 = patientAddress2
          ? patientAddress2
          : "[patient's address line 2]";
        patientAddress3 = patientAddress3
          ? patientAddress3
          : "[patient's address line 3]";
        patientPostcode = patientPostcode
          ? patientPostcode
          : "[patient's postcode]";
        patientEmail = patientEmail ? patientEmail : "[patient's email]";
        patientSex = patientSex ? patientSex : "[patient's sex]";

        await updateDoc(recordingDocRef, {
          transcription: updatedTranscript,
        });
      } else {
        console.log("Recording document does not exist");
      }

      const newSummaryRef = await addDoc(regeneratedSummaryColRef, {
        templateID: regenerateSelectedTemplate,
        templateCategory: regenerateSelectedCategory,
        templateIsPreset: regenerateIsPreset,
        templateDisplayName: regenerateSelectedDisplayName,
        noteType: regenerateSelectedDisplayName,
        // instruction: userInfoRegenerateInstruction,
        instruction: regenerateInstruction,
        summaryChange: "Regenerated",
        timestamp: Timestamp.now(),
        summary: "",
        newTranscript: newTranscript || "",
      });

      const regenerateSummaryDocId = newSummaryRef.id;

      axios
        .post(
          "https://europe-west2-motics-consultations.cloudfunctions.net/regenerate-summary",
          {
            userId,
            recId,
            regenerateSummaryDocId,
            practitionerTitle,
            practitionerName,
            practitionerClinic,
            practitionerClinicAddress1,
            practitionerClinicAddress2,
            practitionerClinicAddress3,
            practitionerClinicPostCode,
            practitionerClinicContactNumber,
            patientName,
            patientDOB,
            patientAddress1,
            patientAddress2,
            patientAddress3,
            patientPostcode,
            patientEmail,
            patientSex,
            meddbasePID,
            meddbasePV1,
          }
        )
        .then(async (response) => {
          if (response) {
            // NOTE: This is a temporary workaround to fetch the regenerated summary. We should be receiving the summary from the cloud function response but response.data is null. Could be due to CORS issue where the response is not being sent back to the client.
            const summaryDocRef = doc(
              db,
              `users/${userId}/recordings/${recId}/summaryVersions`,
              regenerateSummaryDocId
            );
            const summaryDocSnapshot = await getDoc(summaryDocRef);
            if (summaryDocSnapshot.exists()) {
              const summaryData = summaryDocSnapshot.data();
              const summary = summaryData.summary;
              // console.log(`Regenerated Summary:
              // ${summary}`);
              // console.log(
              //   "processed regenerated summary: ",
              //   processSummary(regenerateSelectedDisplayName || "", summary)
              // );
              // console.log(`Regenerated Summary <br>: ${summary}`);
              const process_time = summaryData.process_time;

              const processedRegeneratedSummary = processSummary(
                regenerateSelectedDisplayName || "",
                summary
              );
              const newSummaryVersion = {
                id: regenerateSummaryDocId,
                timestamp: Timestamp.now(),
                summary: processedRegeneratedSummary,
                htmlSummary: summary,
                noteType: regenerateSelectedDisplayName,
                instruction: regenerateInstruction,
                process_time: process_time,
                summaryChange: "Regenerated",
              };
              // console.log("Summary received:", summary);
              setRecordings((prevRecordings) =>
                prevRecordings.map((recording) => {
                  if (recording.id === recId) {
                    let updatedSummaryVersions: Summary[] = [];

                    if (
                      recording.summaryVersions &&
                      recording.summaryVersions.length === 0
                    ) {
                      const originalSummaryVersion = {
                        id: "original",
                        timestamp: recording.timestamp,
                        summary: processSummary("", recording.summary),
                        htmlSummary: recording.summary,
                        noteType: recording.noteType,
                        instruction: "",
                        process_time: 0,
                        summaryChange: "Original",
                      };
                      updatedSummaryVersions.push(originalSummaryVersion);
                    } else {
                      updatedSummaryVersions.push(
                        ...(recording.summaryVersions || [])
                      );
                    }

                    updatedSummaryVersions.push(newSummaryVersion);
                    const updatedLength = updatedSummaryVersions.length;

                    return {
                      ...recording,
                      summary: summary,
                      noteType: regenerateSelectedDisplayName,
                      summaryVersions: updatedSummaryVersions,
                      summaryVersionsLength: updatedLength,
                      summaryVersionsCurrentIndex: updatedLength - 1,
                    };
                  }
                  return recording;
                })
              );

              setShowRegenerateSuccessfulAlert(true);
              setTimeout(() => setShowRegenerateSuccessfulAlert(false), 5000);
            } else {
              // console.log("Summary document does not exist");
            }
          } else {
            console.error("Summary data is missing from the response");
          }
        })
        .catch((error) => {
          console.error("Error making request:", error);
          alert("Failed to regenerate the summary. Please try again.");
        })
        .finally(() => {
          setLoadingRegenerateSummary(false);
          setRegenerateInstruction("");
        });
    } catch (error) {
      console.error("Error saving regenerated summary:", error);
      alert("Failed to regenerate the summary. Please try again.");
      setLoadingRegenerateSummary(false);
      setRegenerateInstruction("");
    }
  };

  const getPatientName = (patientId: string) => {
    // const patient = patients.find((p) => p.id === patientId);
    const patient = searchPatientResults.find((p) => p.id === patientId);
    return patient ? `${patient.first_name} ${patient.last_name}` : "Unknown";
  };

  const filteredRecordings = recordings
    .filter((rec: Recording) => {
      let matches = false;

      if (rec.summary) {
        matches =
          matches ||
          rec.summary.toLowerCase().includes(searchValue.toLowerCase());
      }

      if (rec.summaryVersions) {
        rec.summaryVersions.forEach((summaryVersion) => {
          matches =
            matches ||
            summaryVersion.summary
              .toLowerCase()
              .includes(searchValue.toLowerCase());
        });
      }

      if (rec.transcription) {
        matches =
          matches ||
          rec.transcription.toLowerCase().includes(searchValue.toLowerCase());
      }

      if (rec.sessionNotes) {
        matches =
          matches ||
          rec.sessionNotes.toLowerCase().includes(searchValue.toLowerCase());
      }

      if (rec.clippeOutput) {
        matches =
          matches ||
          rec.clippeOutput.toLowerCase().includes(searchValue.toLowerCase());
      }

      if (rec.timestamp) {
        const formatted = formatDate(rec.timestamp.toDate().toISOString());
        matches =
          matches ||
          formatted.date.toLowerCase().includes(searchValue.toLowerCase());
        matches =
          matches ||
          formatted.time.toLowerCase().includes(searchValue.toLowerCase());
      }

      if (rec.progress) {
        const progress: string =
          rec.progress === "Summary generated" ? "Completed" : "In Progress";
        matches =
          matches || progress.toLowerCase().includes(searchValue.toLowerCase());
      }

      if (rec.name) {
        matches =
          matches || rec.name.toLowerCase().includes(searchValue.toLowerCase());
      }

      return matches;
    })
    .sort((a: Recording, b: Recording) => {
      if (sortFavourited === "favourited") {
        if (a.isFavourited && !b.isFavourited) return -1;
        if (!a.isFavourited && b.isFavourited) return 1;
        return b.timestamp.seconds - a.timestamp.seconds; // Same favorited status, sort by timestamp
      } else if (sortFavourited === "unfavourited") {
        if (!a.isFavourited && b.isFavourited) return -1;
        if (a.isFavourited && !b.isFavourited) return 1;
        return b.timestamp.seconds - a.timestamp.seconds; // Same unfavorited status, sort by timestamp
      }
      // When sortFavourited is "none" or any other value, sort by timestamp
      return b.timestamp.seconds - a.timestamp.seconds;
    });

  function formatDate(isoString: string): { date: string; time: string } {
    const dateObj = new Date(isoString);

    // Extract date components
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
    const year = dateObj.getFullYear();

    // Extract time components and convert to 12-hour format
    let hours = dateObj.getHours();
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    // Convert hour from 24-hour to 12-hour format
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'

    // Add leading zero to single-digit hour
    const formattedHours = String(hours).padStart(2, "0");

    return {
      date: `${day}-${month}-${year}`,
      time: `${formattedHours}:${minutes} ${ampm}`,
    };
  }

  const formatDob = (dob: string) => {
    try {
      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(dob));
    } catch (error) {
      return "Not provided";
    }
  };

  const handleCopyToClipboard = (content: string) => {
    // const element = document.createElement("textarea");
    // element.value = summary.replace(/<[^>]+>/g, ""); // Remove HTML tags from the summary
    // document.body.appendChild(element);
    // element.select();
    // navigator.clipboard.writeText(element.value);
    // document.body.removeChild(element);
    const stripHtmlTags = (html: string): string => {
      const div = document.createElement("div");
      div.innerHTML = html;
      return div.textContent || div.innerText || "";
    };

    navigator.clipboard.write([
      new ClipboardItem({
        "text/html": new Blob([`<style>p { margin: 0; }</style>${content}`], {
          type: "text/html",
        }),
        "text/plain": new Blob([stripHtmlTags(content)], {
          type: "text/plain",
        }),
      }),
    ]);

    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 1000);
  };

  const handleDownloadDocument = async (summary: string, datetime: string) => {
    setIsDownloadingDocument(true);
    const cleanedSummary = summary
      .replace(/<h1>.*?<\/h1>/g, "")
      .replace(/^\s*[\r\n]/gm, "");
    // console.log("summary to document: ", summary);

    try {
      const requestBody = {
        text: cleanedSummary,
        userID: uid,
      };

      const response = await fetch(
        "https://europe-west2-motics-consultations.cloudfunctions.net/text_to_docx",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      // Assuming the response is a downloadable file
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `motics_summary_${datetime.replace(/:/g, "-")}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Failed to download the document. Please try again.");
    } finally {
      setIsDownloadingDocument(false);
    }
  };

  const handleReformatClick = (
    summary: string,
    transcription: string,
    date: string,
    time: string
  ) => {
    setCurrentNoteDateTime({ date: date, time: time });
    setSidebarVisible(true); // Make sidebar visible
    // Pass the summary and transcription for optional inclusion
    setSummaryToReformat(summary);
    setTranscriptionToReformat(transcription);
    setChatInput("");
  };

  // const TipTapComponent = ({ content }) => {
  //   const [editor, setEditor] = useState(null);

  //   useEffect(() => {
  //     if (!editor) return;

  //     editor.commands.setContent(content);
  //   }, [editor, content]);

  //   const editor = useEditor({
  //     content: content,
  //     onUpdate: ({ editor }) => {
  //       // Update the content in your state or wherever needed
  //     },
  //   });

  //   return (
  //     <div>
  //       <EditorMenuBar editor={editor} />
  //       <EditorContent editor={editor} />
  //       <Editor editor={editor} />
  //     </div>
  //   );
  // };

  function formatWithHtml(text: string) {
    // Convert headers
    text = text.replace(/\*\*(.+?)\*\*/g, "<h2>$1</h2>");

    // Convert unordered lists
    text = text
      .replace(/^- (.+)/gm, "<li>$1</li>")
      .replace(/(<li>.+?<\/li>)/gs, "<ul>$1</ul>");

    // Convert paragraphs
    text = text
      .replace(/<h2>.+?<\/h2>|<ul>.+?<\/ul>/gs, (match: string) => `${match}\n`)
      .replace(/^(?!<h2>|<ul>|<\/ul>).+$/gm, "<p>$&</p>");

    // Handle specific formatting if needed (e.g., for 'Reassessment Post-Treatment' italics)
    text = text.replace(/\/\/ (.+?):/g, "<p><em>$1:</em></p>");

    return text;
  }

  const handleSearchAssignPatient = async (
    first_name: string,
    last_name: string
  ) => {
    setLoadingSearchPatientResults(true);

    const full_name = `${first_name ? first_name + " " : ""}${last_name}`;

    try {
      setSelectedAssignPatient(null);
      // console.log("Searching for: ", full_name);
      const cloudFunctionResponse = await axios.post(
        "https://europe-west2-motics-consultations.cloudfunctions.net/cliniko-fetch-patients",
        { uid, first_name: first_name.trim(), last_name: last_name.trim() }
      );

      // console.log("Search patient results: ", cloudFunctionResponse.data);

      const cloudFunctionPatients: Patient[] = (
        cloudFunctionResponse.data || []
      ).map((patient: any) => ({
        ...patient,
        dob: new Date(patient.date_of_birth),
        phone:
          patient.patient_phone_numbers.length > 0
            ? patient.patient_phone_numbers[0].normalized_number
            : "",
        phone_type:
          patient.patient_phone_numbers.length > 0
            ? patient.patient_phone_numbers[0].phone_type
            : "",
      }));
      setSearchDone(true);
      setCloudFunctionPatients(cloudFunctionPatients);
      setSearchPatientResults(cloudFunctionPatients);
      setSearchFirstName("");
      setSearchLastName("");
    } catch (error) {
      console.error("Error searching for patients:", error);
      // Handle error state or display error message
    } finally {
      setLoadingSearchPatientResults(false);
    }
  };

  const handleDeleteRecording = async (recordingId: string) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this session? This will delete the summary, transcript and notes for this session."
    );
    if (confirmDelete) {
      try {
        // Delete the recording document on Firebase
        await deleteDoc(doc(db, `users/${uid}/recordings`, recordingId));
        // Update setRecordings to reflect the deletion
        setRecordings((prevRecordings) =>
          prevRecordings.filter((rec) => rec.id !== recordingId)
        );
      } catch (error) {
        console.error("Error deleting recording:", error);
        alert("Failed to delete the recording. Please try again.");
      }
      setExpandedRecording(null);
    }
  };

  const handleAssignToPatient = async (patient: Patient, location: string) => {
    setIsAssigningToPatient(true);
    if (!selectedAssignPatient) {
      alert("Selected patient not found. Please try again.");
      return;
    }

    // Extract necessary data from the modal state
    const { recordingId, noteType, summary } = showAssignToPatientModal;
    // console.log("Summary", summary);
    const htmlSummary = processSummary(noteType, summary);
    // console.log("newHtmlSummary", newHtmlSummary);
    if (!recordingId) {
      console.error("Recording ID is undefined.");
      return;
    }

    try {
      const recordingDocRef = doc(db, `users/${uid}/recordings`, recordingId);
      const recordingDoc = await getDoc(recordingDocRef);

      let timestamp;
      if (recordingDoc.exists()) {
        const recordingData = recordingDoc.data();
        timestamp = recordingData.timestamp.toDate();
      } else {
        // console.log("Recording document does not exist");
        timestamp = new Date();
      }

      await updateDoc(recordingDocRef, {
        patientId: selectedAssignPatient.id,
        patientName: `${selectedAssignPatient.first_name} ${selectedAssignPatient.last_name}`,
        patientDOB: selectedAssignPatient.dob,
        patientAddress1: selectedAssignPatient.address_1
          ? selectedAssignPatient.address_1
          : "Not provided",
        patientAddress2: selectedAssignPatient.address_2
          ? selectedAssignPatient.address_2
          : "Not provided",
        patientAddress3: selectedAssignPatient.address_3
          ? selectedAssignPatient.address_3
          : "Not provided",
        patientPostcode: selectedAssignPatient.post_code || "Not provided",
        patientEmail: selectedAssignPatient.email || "Not provided",
        patientSex: selectedAssignPatient.sex || "Not provided",
      });
      const patientNotesCollectionRef = collection(
        db,
        `users/${uid}/patients/${selectedAssignPatient.id}/patientNotes`
      );
      const patientNoteData = {
        timestamp: timestamp,
        noteType: noteType,
        summary: summary,
        htmlSummary: htmlSummary,
        recordingId: recordingId,
      };

      const patientNoteQuery = query(
        patientNotesCollectionRef,
        where("recordingId", "==", recordingId)
      );
      const querySnapshot = await getDocs(patientNoteQuery);

      if (querySnapshot.empty) {
        await addDoc(patientNotesCollectionRef, patientNoteData);
      } else {
        // console.log(
        //   "Document with recordingId already exists in patient notes collection"
        // );
      }
      try {
        const clinikoResponse = await axios.post(
          "https://europe-west2-motics-consultations.cloudfunctions.net/cliniko-push-treatment-notes",
          {
            uid,
            clinikoApiKey,
            patient_id: selectedAssignPatient.id,
            title: `Motics Copilot Note: ${new Date().toLocaleString()}`,
            note_content: htmlSummary,
            // location: location,
          }
        );
        // console.log("Cliniko response:", clinikoResponse);
        // Update the patient's note document with the Cliniko note ID
        const clinikoNoteID = clinikoResponse.data.id;
        // console.log("Cliniko note ID:", clinikoNoteID);
        const recordingRef = doc(db, `users/${uid}/recordings`, recordingId);
        await updateDoc(recordingRef, { clinikoNoteID: clinikoNoteID });
        // const patientNoteRef = doc(
        //   db,
        //   `users/${uid}/patients/${selectedPatient.id}/patientNotes`,
        //   recordingId
        // );
        // await updateDoc(patientNoteRef, { clinikoNoteID });
      } catch (error) {
        console.error("Error pushing summary to Cliniko:", error);
      }

      // console.log("Summary assigned to patient successfully.");
      alert("Summary assigned to patient successfully.");
    } catch (error) {
      console.error("Error updating recording document:", error);
      alert("Failed to assign the summary to the patient. Please try again.");
    }
    setIsAssigningToPatient(false);
    setSelectedAssignPatient(null);
    setSearchPatientResults([]);
    setIsPatientInfoCorrect(false);
    setSearchDone(false);
    setShowAssignToPatientModal({
      isVisible: false,
      recordingId: "",
      noteType: "",
      summary: "",
      htmlSummary: "",
      isAssigned: false,
      patientName: "",
      assignedPatient: null,
    });
  };

  const openAssignToPatientModal = (recording: Recording) => {
    const isAssigned = recording.patientId != null;
    const assignedPatient = isAssigned
      ? searchPatientResults.find(
          (patient) => patient.id === recording.patientId
        )
      : null;
    // const assignedPatient = isAssigned
    //   ? patients.find((patient) => patient.id === recording.patientId)
    //   : null;

    // Capture the currently active summary version
    const activeSummaryVersion =
      recording.summaryVersions &&
      recording.summaryVersions.length > 0 &&
      recording.summaryVersionsCurrentIndex !== undefined
        ? recording.summaryVersions[recording.summaryVersionsCurrentIndex]
            .summary
        : recording.summary; // Fallback to the main summary if no versions are available

    const activeHtmlSummaryVersion =
      recording.summaryVersions &&
      recording.summaryVersions.length > 0 &&
      recording.summaryVersionsCurrentIndex !== undefined
        ? recording.summaryVersions[recording.summaryVersionsCurrentIndex]
            .htmlSummary
        : recording.htmlSummary;

    const activeNoteType =
      recording.summaryVersions &&
      recording.summaryVersions.length > 0 &&
      recording.summaryVersionsCurrentIndex !== undefined
        ? recording.summaryVersions[recording.summaryVersionsCurrentIndex]
            .noteType
        : recording.noteType;

    setShowAssignToPatientModal({
      isVisible: true,
      recordingId: recording.id,
      noteType: activeNoteType,
      summary: activeSummaryVersion, // Use the active summary version
      htmlSummary: activeHtmlSummaryVersion,
      isAssigned: isAssigned,
      patientName: recording.patientName || "",
      assignedPatient: assignedPatient,
    });
  };

  const changeVersion = (direction: string, recordingId: string) => {
    setRecordings((prevRecordings) => {
      return prevRecordings.map((rec) => {
        if (rec.id === recordingId) {
          // Ensure summaryVersions and summaryVersionsCurrentIndex are defined
          if (
            rec.summaryVersions &&
            rec.summaryVersionsCurrentIndex !== undefined
          ) {
            const totalVersions = rec.summaryVersions.length;
            let newIndex = rec.summaryVersionsCurrentIndex;

            if (direction === "next" && newIndex < totalVersions - 1) {
              newIndex += 1;
            } else if (direction === "prev" && newIndex > 0) {
              newIndex -= 1;
            }

            return {
              ...rec,
              summaryVersionsCurrentIndex: newIndex,
            };
          }
        }
        return rec;
      });
    });
  };

  function camelCaseToWords(str: string): string {
    // Split the string into words based on capital letters.
    const words = str.match(/[A-Z]?[a-z]+|[A-Z]+(?![a-z])/g) || [];

    // Capitalize the first word and join the rest of the words with spaces.
    const result = words
      .map((word, index) =>
        index === 0
          ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          : word
      )
      .join(" ");

    return result;
  }

  const renderRegenerateSummaryModal = () =>
    // recId: string,
    // recSummary: string,
    // recTimeStamp: any,
    // index: number
    {
      if (!openRegenerateDialog) {
        return null;
      }
      const recId = toRegenerateRecId;
      const recSummary = toRegenerateRecSummary;
      const recTimeStamp = toRegenerateRecTimeStamp;
      const index = toRegenerateIndex;
      const handleTemplateChange = async (e: SelectChangeEvent) => {
        const templateId = e.target.value;
        setRegenerateSelectedTemplate(templateId);

        if (!regenerateSelectedCategory) return;

        const template =
          combinedTemplates[regenerateSelectedCategory] &&
          combinedTemplates[regenerateSelectedCategory].find(
            (t) => t.id === templateId
          );
        if (template) {
          setRegenerateSelectedDisplayName(template.displayName);
          const isPresetTemplate = await checkIfPreset(
            template.id,
            regenerateSelectedCategory
          );

          setRegenerateIsPreset(isPresetTemplate);
        }
      };

      async function checkIfPreset(
        templateId: string,
        category: keyof Templates
      ): Promise<boolean> {
        // Simulate checking logic
        return new Promise((resolve) => {
          let isPreset = false;
          if (presetTemplates[category]) {
            isPreset = presetTemplates[category].some(
              (presetTemplate) => presetTemplate.id === templateId
            );
          }
          resolve(isPreset);
        });
      }

      // if (recNoteType) {
      //   setChangeNoteType(recNoteType);
      // }
      return (
        <div className="box-sizing-default modal-background">
          <div
            className="box-sizing-default modals"
            id="regenerate-summary-modal"
            style={{ padding: "0" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
                padding: "1rem 2rem 0 2rem",
              }}
            >
              <div style={{ fontSize: "2rem", fontWeight: "500" }}>
                Regenerate Summary
              </div>
              <button
                className="small-icon-button"
                onClick={handleCloseRegenerateDialog}
                style={{ alignSelf: "center" }}
              >
                <img
                  src={xMarkIcon}
                  className="small-icon-button"
                  alt="close"
                />
              </button>
            </div>
            <div style={{ overflow: "auto", padding: "1rem 2rem" }}>
              {/* <p>Please select summary regeneration note type.</p> */}
              <div
                style={{
                  fontSize: "1rem",
                  fontWeight: "bold",
                  textAlign: "center",
                  marginBottom: "1rem",
                }}
              >
                Choose a template
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "2rem",
                  marginBottom: "2rem",
                  width: "100%",
                }}
              >
                <div style={{ width: "40%" }}>
                  <div>Note Category</div>
                  <FormControl fullWidth size="small">
                    <Select
                      labelId="demo-select-small-label"
                      id="demo-select-small"
                      value={regenerateSelectedCategory}
                      onChange={(e: SelectChangeEvent) => {
                        setRegenerateSelectedCategory(
                          e.target.value as keyof Templates
                        );
                        setRegenerateSelectedTemplate("");
                      }}
                    >
                      {Object.keys(combinedTemplates).map((category) => (
                        <MenuItem key={category} value={category}>
                          {camelCaseToWords(category)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div style={{ width: "40%" }}>
                  <div>Note Template</div>
                  <FormControl fullWidth size="small">
                    <Select
                      labelId="demo-select-small-label"
                      id="demo-select-small"
                      value={regenerateSelectedTemplate || ""}
                      onChange={handleTemplateChange}
                      disabled={!regenerateSelectedCategory}
                    >
                      {regenerateSelectedCategory &&
                        combinedTemplates[regenerateSelectedCategory].map(
                          (template) => (
                            <MenuItem key={template.id} value={template.id}>
                              {template.displayName}
                            </MenuItem>
                          )
                        )}
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                Add to transcript via audio recording:
                <RegenerateAudioRecorder
                  mode="generate"
                  onAudioUrl={handleAudioUrl}
                  onRecordingClick={handleRecordingClick}
                  userId={uid}
                  recordingId={recId}
                  sessionNotesUpload={sessionNotesUpload}
                  noteTemplate={regenerateSelectedDisplayName}
                  templateID={regenerateSelectedTemplate}
                  templateCategory={regenerateSelectedCategory}
                  templateIsPreset={regenerateIsPreset}
                  newTranscript={newTranscript}
                  setNewTranscript={setNewTranscript}
                  setIsTranscribing={setIsTranscribing}
                  isTranscribing={isTranscribing}
                />
              </div>
              {/* {regenerateSelectedCategory && (
            <>
              {console.log(
                "regenerateSelectedCategory:",
                regenerateSelectedCategory
              )}
              {console.log(
                "regenerateSelectedTemplate:",
                regenerateSelectedTemplate
              )}
            </>
          )} */}
              {/* <div
            style={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: "10px",
              marginBottom: "1rem",
            }}
          >
            {[
              "Adaptive AI",
              "SOAP Note",
              "SOAP Note (Short)",
              "Letter to Patient",
              "Letter to GP",
              "Referral to Speciality",
              "Custom",
            ].map((type) => (
              <button
                className={`button-classic ${
                  (recordings[index].noteType || selectedNoteType) === type
                    ? "button-classic-green"
                    : ""
                }`}
                onClick={() => {
                  recordings[index].noteType = type;
                  setRegenerateNoteType(type);
                }}
              >
                {type}
              </button>
            ))}
          </div> */}
              <p style={{ marginTop: "1rem", marginBottom: "0" }}>
                Please enter any additional instructions.
              </p>
              <div
                className="box-sizing-default alert-text"
                style={{ marginTop: "0" }}
              >
                Please note that previous additional instructions will be
                overwritten when regenerating the summary. Please include any
                previous instructions in the new instructions if they are
                needed.
              </div>
              <TextField
                style={{ marginBottom: "1rem" }}
                label="Additional instructions (e.g., 'Please include the patient's name, John Smith, in the letter.')"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                value={regenerateInstruction}
                onChange={(e) => setRegenerateInstruction(e.target.value)}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  margin: "1rem",
                }}
              >
                <button
                  className="button-copy"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "auto",
                    color: "white",
                    cursor: recording ? "not-allowed" : "pointer",
                  }}
                  disabled={recording || isTranscribing}
                  onClick={() =>
                    handleRegenerateSummary(
                      recId,
                      recSummary,
                      recTimeStamp || new Date()
                    )
                  }
                >
                  <AutoFixHighIcon sx={{ mr: 1 }} />
                  Regenerate
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    };

  useEffect(() => {
    // If there's only one search result, pre-select it in the dropdown
    if (searchPatientResults.length === 1) {
      setSelectedPatientIndex("0"); // Pre-select the first (and only) option
      setSelectedAssignPatient(searchPatientResults[0]);
    } else {
      setSelectedPatientIndex(""); // Reset or set to a default value as needed
    }
  }, [searchPatientResults]);

  const renderAssignToPatientModal = () => {
    const { isVisible, isAssigned, assignedPatient, patientName } =
      showAssignToPatientModal;
    if (!isVisible) return null;

    const handleSelectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedId = e.target.value;
      setSelectedPatientIndex(selectedId);
      const patient = searchPatientResults[parseInt(selectedId, 10)];
      setSelectedAssignPatient(patient || null);
    };

    return (
      <div className="box-sizing-default modal-background">
        <div className="box-sizing-default modals" id="assign-patient-modal">
          <button
            className="small-icon-button"
            onClick={() => {
              setShowAssignToPatientModal({
                isVisible: false,
                recordingId: "",
                noteType: "",
                summary: "",
                htmlSummary: "",
                isAssigned: false,
                patientName: "",
                assignedPatient: null,
              });
              setSearchDone(false);
              setSelectedAssignPatient(null);
              setSearchPatientResults([]);
              setSearchFirstName("");
              setSearchLastName("");
            }}
          >
            <img src={xMarkIcon} alt="close" />
          </button>
          <h2>Push to Cliniko</h2>
          {/* <p>Assign the generated note to a patient.</p> */}
          {isAssigned && patientName && (
            <div className="alert-text">
              Please note that this summary has already been assigned to{" "}
              {patientName}.
            </div>
          )}
          <p>
            Search a patient by entering their first name or last name, or both.
          </p>
          <div
            className="box-sizing-default welcome-form"
            style={{ padding: "0" }}
          >
            <div
              style={{
                boxSizing: "border-box",
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <input
                className="box-sizing-default"
                style={{ width: "48%" }}
                type="text"
                name="firstName"
                placeholder="First name"
                value={searchFirstName}
                onChange={(e) => setSearchFirstName(e.target.value)}
              />
              <input
                className="box-sizing-default"
                style={{ width: "48%" }}
                type="text"
                name="lastName"
                placeholder="Last name"
                value={searchLastName}
                onChange={(e) => setSearchLastName(e.target.value)}
              />
            </div>
            <button
              className="button-copy"
              style={{
                display: "flex",
                alignItems: "center",
                alignSelf: "center",
                cursor: loadingRegenerateSummary ? "not-allowed" : "pointer",
              }}
              disabled={loadingSearchPatientResults}
              onClick={() => {
                if (!searchFirstName && !searchLastName) {
                  alert("Please enter at least one name to search.");
                } else {
                  handleSearchAssignPatient(searchFirstName, searchLastName);
                }
              }}
            >
              {loadingSearchPatientResults && (
                <img
                  src={loadingSpinner}
                  alt="Loading"
                  style={{
                    width: "1.5rem",
                    marginRight: "0.8rem",
                  }}
                />
              )}
              Search
            </button>
          </div>
          {searchPatientResults && searchPatientResults.length > 0 ? (
            <div style={{ marginTop: "1rem" }}>
              <p>Patient search results</p>
              <select
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "0.6rem",
                  border: "0.05rem solid #ccc",
                  borderRadius: "0.5rem",
                  overflowY: "auto",
                }}
                onChange={handleSelectionChange}
                value={selectedPatientIndex || ""} // Fallback to an empty string if undefined
              >
                <option value="" disabled>
                  Select a patient
                </option>
                {searchPatientResults.map((patient, index) => (
                  <option key={index} value={index}>
                    {`${patient.first_name} ${patient.last_name}`} -{" "}
                    {patient.dob
                      ? `(${formatDob(patient.dob.toString())})`
                      : ""}
                  </option>
                ))}
              </select>
            </div>
          ) : searchPatientResults &&
            searchPatientResults.length === 0 &&
            searchDone ? (
            <p>No patient results found.</p>
          ) : null}
          {/* <Autocomplete
            value={selectedAssignPatient}
            onChange={(event, newValue: Patient | null) => {
              setSelectedAssignPatient(newValue);
            }}
            inputValue={searchInputValue}
            onInputChange={(event, newInputValue) => {
              setSearchInputValue(newInputValue);
            }}
            options={filteredSearchPatients}
            getOptionLabel={(patient) =>
              `${patient.first_name} ${patient.last_name}`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Enter at least 3 characters to search for a patient"
                variant="outlined"
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, patient) => (
              <Box
                component="li"
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
                {...props}
              >
                <Typography
                  sx={{ textAlign: "left", flexGrow: 1 }}
                >{`${patient.first_name} ${patient.last_name}`}</Typography>
                <Typography sx={{ textAlign: "right", color: "grey" }}>
                {patient.dob instanceof Date ? `(${formatDob(patient.dob.toISOString())})` : ""}
                </Typography>
              </Box>
            )}
          /> */}

          {selectedAssignPatient && (
            <>
              <div style={{ marginTop: "2rem" }}>
                Please confirm the patient's information below before assigning.
              </div>
              <div
                style={{
                  padding: "1rem",
                  backgroundColor: "var(--color-brand-dark-green)",
                  color: "white",
                  border: "0.08rem solid silver",
                  borderRadius: "0.25rem",
                }}
              >
                <div>
                  <strong>Name: </strong>
                  {selectedAssignPatient.first_name}{" "}
                  {selectedAssignPatient.last_name}
                </div>
                <div>
                  <strong>Date of Birth: </strong>
                  {selectedAssignPatient.dob
                    ? formatDob(
                        selectedAssignPatient.dob instanceof Date
                          ? selectedAssignPatient.dob.toISOString()
                          : ""
                      )
                    : "Not provided"}
                </div>
                <div>
                  <strong>Sex: </strong>
                  {selectedAssignPatient.sex || "Not provided"}
                </div>
                <div>
                  <strong>Address: </strong>
                  {`${
                    selectedAssignPatient.address_1
                      ? selectedAssignPatient.address_1
                      : "Not provided"
                  }${
                    selectedAssignPatient.address_2
                      ? `, ${selectedAssignPatient.address_2}`
                      : ""
                  }${
                    selectedAssignPatient.address_3
                      ? `, ${selectedAssignPatient.address_3}`
                      : ""
                  }`}
                </div>
                <div>
                  <strong>Postcode: </strong>
                  {selectedAssignPatient.post_code || "Not provided"}
                </div>
                <div>
                  <strong>Email: </strong>
                  {selectedAssignPatient.email || "Not provided"}
                </div>
              </div>
              <div className="alert-text">
                If the patient's information is incorrect, please update the
                patient's information in Cliniko.
              </div>
              <div style={{ fontSize: "0.8rem" }}>
                <label>
                  <input
                    type="checkbox"
                    value="isPatientInfoCorrect"
                    onChange={(e) => setIsPatientInfoCorrect(e.target.checked)}
                  />
                  Confirm patient information is correct.
                </label>
              </div>
            </>
          )}
          {/* {selectedAssignPatient && isPatientInfoCorrect && (
             <div style={{ marginTop: "1rem" }}>
             <p>Please choose where you would like to push this summary to.</p>
             <FormControl fullWidth>
               <InputLabel id="document-type-label">
                 Select location
               </InputLabel>
               <Select
                 // style={{marginTop:"1rem"}}
                 labelId="document-type-label"
                 id="documentType"
                 value={documentType}
                 label="Select location"
                 onChange={(event) => setDocumentType(event.target.value)}
               >
                 <MenuItem value="treatmentNotes">Treatment Notes</MenuItem>
                 <MenuItem value="files">Files</MenuItem>
          </Select>

          {isCloudFunctionPatient && (
            <div>
              <p>
                Would you like to push this summary to{" "}
                {getPatientName(selectedPatientId)}'s Cliniko record?
              </p>
              <div>
                <label>
                  Yes
                  <input
                    type="radio"
                    value="yes"
                    name="clinikoPush"
                    onChange={() => setPushToCliniko(true)}
                  />
                </label>
                <label>
                  No
                  <input
                    type="radio"
                    value="no"
                    name="clinikoPush"
                    onChange={() => setPushToCliniko(false)}
                  />
                </label>
              </div>
            </div>
          )} */}
          {selectedAssignPatient && isPatientInfoCorrect && (
            <button
              className="button-primary"
              disabled={isAssigningToPatient}
              style={{ marginTop: "2rem" }}
              onClick={() => {
                if (isPatientInfoCorrect && selectedAssignPatient !== null) {
                  handleAssignToPatient(
                    selectedAssignPatient,
                    "treatmentNotes"
                  );
                } else {
                  if (!isPatientInfoCorrect) {
                    alert(
                      "Please confirm that the patient information is correct before saving."
                    );
                  } else if (!selectedAssignPatient) {
                    alert("Please select a patient before saving.");
                  }
                }
              }}
            >
              Save
            </button>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      // Remove the space after a delay
      setIsFocused(false);
    }, 1000); // Adjust the delay time as needed

    return () => clearTimeout(timeout);
  }, []);

  const toggleStar = async (id: string) => {
    const updatedRecordings = recordings.map((rec) => {
      if (rec.id === id) {
        // Prepare the updated recording object
        const updatedRec = { ...rec, isFavourited: !rec.isFavourited };

        // Reference to the Firestore document
        const recordingRef = doc(db, `users/${uid}/recordings`, id);

        // Update the document in Firestore
        updateDoc(recordingRef, { isFavourited: updatedRec.isFavourited })
          // .then(() => console.log("Favourited status updated successfully"))
          .catch((error) =>
            console.error("Error updating favourited status", error)
          );

        return updatedRec;
      }
      return rec;
    });

    setRecordings(updatedRecordings); // Update the state with the modified array
  };

  return (
    <>
      {pageLoading ? ( // Conditionally render loading spinner or message
        <div>Loading...</div>
      ) : (
        <div className="box-sizing-default tab-content-container">
          <h2>History</h2>
          {meddbasePID && (
            <div
              style={{
                backgroundColor: "var(--color-brand-dark-green)",
                color: "white",
                padding: "0.5rem",
                marginBottom: "1rem",
                borderRadius: "0.5rem",
              }}
            >
              <p
                style={{
                  color: "white",
                  fontSize: "0.8rem",
                }}
              >
                <strong>Current Selected Client:</strong>{" "}
                {meddbasePID.patientName.givenName}{" "}
                {meddbasePID.patientName.familyName}
              </p>
            </div>
          )}
          <div>
            <input
              className="box-sizing-default search-bar"
              type="text"
              placeholder="Search..."
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
            />
            {/* Add show favourited only */}
            <div style={{ margin: "0.2rem 1rem 1rem" }}>
              <label htmlFor="sortPreference">Sort by: </label>
              <select
                id="sortPreference"
                value={sortFavourited}
                onChange={(e) =>
                  setSortFavourited(e.target.value as SortPreference)
                }
                style={{
                  border: "1px solid silver",
                  borderRadius: "0.5rem",
                  padding: "0.1rem 0.2rem",
                  fontSize: "1rem",
                }}
              >
                <option value="none">Sort by Time</option>
                <option value="favourited">Sort by Favourited</option>
                <option value="unfavourited">Sort by Unfavourited</option>
              </select>
            </div>
          </div>
          {/* <ChatWithAiSidebar
        isVisible={isSidebarVisible}
        toggleSidebar={toggleSidebar}
        chatInput={chatInput}
        setChatInput={setChatInput}
        noteDate={currentNoteDateTime.date}
        noteTime={currentNoteDateTime.time}
        summaryToReformat={summaryToReformat}
        transcriptionToReformat={transcriptionToReformat}
        setSummaryToReformat={setSummaryToReformat} // Pass this to reset in the sidebar
        setTranscriptionToReformat={setTranscriptionToReformat} // Pass this to reset in the sidebar
      /> */}
          <div>
            {filteredRecordings.map((rec: Recording, index: number) => {
              const formatted = formatDate(
                rec.timestamp.toDate().toISOString()
              );

              return (
                <div key={index} className="box-sizing-default history-item">
                  <div
                    style={{
                      boxSizing: "border-box",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "1rem", // Add space between the summary and the details/actions below
                    }}
                  >
                    <div>
                      <strong>Date: </strong>
                      {formatted.date}
                    </div>
                    <div>
                      <strong>Time: </strong>
                      {formatted.time}
                    </div>
                    <div>
                      <strong>Name: </strong>
                      {rec.name}
                    </div>
                    <div>
                      <strong>Summary: </strong>
                      {rec.progress === "Summary generated"
                        ? "Completed"
                        : "In Progress"}
                    </div>
                    <div>
                      {/* Star Icon/Button */}
                      <button
                        className="button-star"
                        onClick={() => toggleStar(rec.id)}
                        style={{
                          fontSize: "1.5rem",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: rec.isFavourited ? "Orange" : "gray", // Use yellow color when starred
                        }}
                        title={rec.isFavourited ? "Unfavourite" : "Favourite"} // Tooltip text
                      >
                        {rec.isFavourited ? "★" : "☆"}
                      </button>
                    </div>
                  </div>
                  {/* AI Summary */}
                  {/* {rec.progress === "Summary generated" && (
                  <div style={{ marginBottom: "1rem" }}>
                    <strong>AI Summary: </strong>
                    {rec.aiSummary ? (
                      <span style={{ fontStyle: "italic" }}>
                        {rec.aiSummary}
                      </span>
                    ) : (
                      <span style={{ fontStyle: "italic" }}>
                        Summary not available.
                      </span>
                    )}
                  </div>
                )} */}
                  {/* Show Summary Button */}
                  {rec.progress === "Summary generated" ? (
                    <div
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <button
                        className="button-history"
                        onClick={() => toggleExpand(index, rec.id)}
                      >
                        {expandedRecording === index
                          ? "Hide Summary"
                          : "Show Summary"}
                      </button>
                    </div>
                  ) : (
                    <div
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <button
                        className="button-history"
                        style={{ backgroundColor: "grey", cursor: "default" }}
                        disabled
                      >
                        No Summary
                      </button>
                    </div>
                  )}
                  {/* Expandable Section for Detailed Summary */}
                  {expandedRecording === index && (
                    <div style={{ marginTop: "2rem" }}>
                      <div>
                        <label
                          style={{
                            margin: "0 1rem 0 1rem",
                            fontWeight: "bold",
                          }}
                        >
                          Recording Name:
                        </label>
                        <input
                          type="text"
                          value={rec.name === "No name" ? "" : rec.name}
                          onChange={(e) => {
                            const newName = e.target.value;
                            setRecordings((prevRecordings) =>
                              prevRecordings.map((recording) =>
                                recording.id === rec.id
                                  ? { ...recording, name: newName }
                                  : recording
                              )
                            );
                            // Update the name in Firebase
                            const recordingRef = doc(
                              db,
                              `users/${uid}/recordings`,
                              rec.id
                            );
                            updateDoc(recordingRef, { name: newName });
                          }}
                          style={{
                            padding: "0.5rem",
                            borderRadius: "0.25rem",
                            border: "1px solid #ccc",
                            fontSize: "1rem",
                            width: "60%",
                          }}
                        />
                      </div>
                      <div>
                        <Box
                          sx={{
                            borderBottom: 1,
                            borderColor: "divider",
                            marginTop: "2rem",
                          }}
                        >
                          <StyledTabs
                            value={activeTabs[rec.id] || 0}
                            onChange={(event, newValue) =>
                              handleTabChange(rec.id, newValue)
                            }
                            aria-label="summary tabs"
                          >
                            <StyledTab label="Summary" />
                            <StyledTab label="Transcript" />
                            <StyledTab label="Notes" />
                            {/* <StyledTab label="CLIPPE" /> */}
                          </StyledTabs>
                        </Box>
                        <div className="box-sizing-default history-item-summary">
                          <div className="button-container">
                            {activeTabs[rec.id] === 0 && (
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  alignSelf: "flex-end",
                                  marginBottom: "1rem",
                                  flexDirection: "column",
                                }}
                              >
                                <div style={{ alignSelf: "flex-end" }}>
                                  <button
                                    className="button-copy"
                                    onClick={() =>
                                      activeTabs[rec.id] === 0
                                        ? handleCopyToClipboard(
                                            rec.summaryVersions &&
                                              rec.summaryVersions.length > 0 &&
                                              rec.summaryVersionsCurrentIndex !==
                                                undefined
                                              ? processSummary(
                                                  rec.summaryVersions[
                                                    rec
                                                      .summaryVersionsCurrentIndex
                                                  ].noteType || "",
                                                  rec.summaryVersions[
                                                    rec
                                                      .summaryVersionsCurrentIndex
                                                  ].summary
                                                )
                                              : processSummary(
                                                  rec.noteType,
                                                  rec.summary
                                                )
                                          )
                                        : activeTabs[rec.id] === 1
                                        ? handleCopyToClipboard(
                                            rec.transcription
                                          )
                                        : activeTabs[rec.id] === 2
                                        ? handleCopyToClipboard(
                                            rec.sessionNotes
                                              ? rec.sessionNotes
                                              : "No notes saved."
                                          )
                                        : activeTabs[rec.id] === 3
                                        ? handleCopyToClipboard(
                                            rec.clippeOutput
                                              ? rec.clippeOutput
                                              : ""
                                          )
                                        : null
                                    }
                                  >
                                    {copied ? "Copied" : "Copy"}
                                  </button>
                                </div>
                                <div>
                                  {clinikoApiKey && (
                                    <button
                                      className="button-copy"
                                      style={{ alignSelf: "left" }}
                                      onClick={() =>
                                        openAssignToPatientModal(rec)
                                      }
                                    >
                                      Push to Cliniko
                                    </button>
                                  )}
                                  {currentUser?.meddbaseUserId &&
                                    currentUser?.meddbaseUserId.length > 0 &&
                                    currentUser?.isMeddbaseUserIdStored && (
                                      <Tooltip
                                        title="This will push the current summary or letter in view to Meddbase as a PDF."
                                        arrow
                                      >
                                        <button
                                          className="button-copy"
                                          style={{ alignSelf: "left" }}
                                          onClick={() =>
                                            setShowPushToMeddbaseModal({
                                              isVisible: true,
                                              recordingId: rec.id,
                                              noteType:
                                                rec.summaryVersions &&
                                                rec.summaryVersions.length >
                                                  0 &&
                                                rec.summaryVersionsCurrentIndex !==
                                                  undefined
                                                  ? rec.summaryVersions[
                                                      rec
                                                        .summaryVersionsCurrentIndex
                                                    ].noteType
                                                  : rec.noteType,
                                              summary:
                                                rec.summaryVersions &&
                                                rec.summaryVersions.length >
                                                  0 &&
                                                rec.summaryVersionsCurrentIndex !==
                                                  undefined
                                                  ? rec.summaryVersions[
                                                      rec
                                                        .summaryVersionsCurrentIndex
                                                    ].summary
                                                  : rec.summary,
                                            })
                                          }
                                          disabled={isPushingToMeddbase}
                                        >
                                          Push to Meddbase
                                        </button>
                                      </Tooltip>
                                    )}
                                  <Tooltip
                                    title="Download the current summary or letter in view as a DOCX file."
                                    arrow
                                  >
                                    <button
                                      className="button-copy"
                                      disabled={isDownloadingDocument}
                                      onClick={() =>
                                        handleDownloadDocument(
                                          rec.summaryVersions &&
                                            rec.summaryVersions.length > 0 &&
                                            rec.summaryVersionsCurrentIndex !==
                                              undefined
                                            ? processSummary(
                                                null,
                                                rec.summaryVersions[
                                                  rec
                                                    .summaryVersionsCurrentIndex
                                                ].summary
                                              )
                                            : processSummary(null, rec.summary),
                                          `${formatted.date}_${formatted.time}`
                                        )
                                      }
                                    >
                                      Download as DOCX
                                    </button>
                                  </Tooltip>
                                </div>
                              </div>
                            )}
                            {/* <button
                        className="button-copy"
                        onClick={() =>
                          handleReformatClick(
                            rec.summary,
                            rec.transcription,
                            formatted.date,
                            formatted.time
                          )
                        }
                      >
                        Chat with AI
                      </button> */}
                          </div>
                          {/* </div> */}
                          {rec.patientId && (
                            <div style={{ alignSelf: "center" }}>
                              <h4>
                                Summary Assigned to Patient:{" "}
                                {/* {getPatientName(rec.patientId)} */}
                                {rec.patientName}
                              </h4>
                            </div>
                          )}
                          {activeTabs[rec.id] === 0 && (
                            <div>
                              <div
                                className={
                                  loadingRegenerateSummary
                                    ? "grey-text-container"
                                    : ""
                                }
                              >
                                {rec.summaryVersions &&
                                rec.summaryVersions.length > 0 &&
                                rec.summaryVersionsCurrentIndex !== undefined &&
                                rec.summaryVersionsCurrentIndex <
                                  rec.summaryVersions.length ? (
                                  <div>
                                    {/* {rec.summaryVersions[
                                rec.summaryVersionsCurrentIndex
                              ] &&
                              rec.summaryVersions[
                                rec.summaryVersionsCurrentIndex
                              ].summary ? ( */}
                                    <TipTap
                                      key={rec.summaryVersionsCurrentIndex}
                                      content={
                                        rec.summaryVersions[
                                          rec.summaryVersionsCurrentIndex
                                        ].summary
                                          ? processSummary(
                                              rec.summaryVersions[
                                                rec.summaryVersionsCurrentIndex
                                              ].noteType || "",
                                              rec.summaryVersions[
                                                rec.summaryVersionsCurrentIndex
                                              ].summary
                                            )
                                          : "No summary found."
                                      }
                                      alwaysFocus={false}
                                      // onFocus={() => setIsFocused(true)}
                                      // onBlur={() => setIsFocused(false)}
                                      onSaveContent={(newContent) => {
                                        if (
                                          rec.summaryVersions &&
                                          rec.summaryVersionsCurrentIndex !==
                                            undefined
                                        ) {
                                          updateContentOnFirebase(
                                            rec.id,
                                            rec.summaryVersions[
                                              rec.summaryVersionsCurrentIndex
                                            ].id,
                                            newContent,
                                            "summary"
                                          );
                                        } else {
                                          updateContentOnFirebase(
                                            rec.id,
                                            "",
                                            newContent,
                                            "summary"
                                          );
                                        }
                                      }}
                                    />
                                    {/* ) : (
                                <div style={{ marginBottom: "1rem" }}>
                                  <TipTap
                                    content={rec.summary}
                                    onSaveContent={(newContent) => {
                                      updateContentOnFirebase(
                                        rec.id,
                                        "",
                                        newContent,
                                        "summary"
                                      );
                                    }}
                                  />
                                </div>
                              )} */}
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        marginTop: "1rem",
                                      }}
                                    >
                                      <button
                                        className={`button-text ${
                                          rec.summaryVersionsCurrentIndex === 0
                                            ? "button-text-disabled"
                                            : ""
                                        }`}
                                        onClick={() =>
                                          changeVersion("prev", rec.id)
                                        }
                                        disabled={
                                          rec.summaryVersionsCurrentIndex === 0
                                        }
                                      >
                                        &lt;
                                      </button>
                                      <span
                                        style={{
                                          color: "grey",
                                          marginLeft: "0.3rem",
                                          marginRight: "0.3rem",
                                        }}
                                      >
                                        {`${
                                          rec.summaryVersionsCurrentIndex +
                                            1 ===
                                          0
                                            ? 1
                                            : rec.summaryVersionsCurrentIndex +
                                              1
                                        } / ${rec.summaryVersions.length}`}
                                      </span>
                                      <button
                                        className={`button-text ${
                                          rec.summaryVersionsCurrentIndex ===
                                          rec.summaryVersions.length - 1
                                            ? "button-text-disabled"
                                            : ""
                                        }`}
                                        onClick={() =>
                                          changeVersion("next", rec.id)
                                        }
                                        disabled={
                                          rec.summaryVersionsCurrentIndex ===
                                          rec.summaryVersions.length - 1
                                        }
                                      >
                                        &gt;
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ marginBottom: "1rem" }}>
                                    <TipTap
                                      content={
                                        rec.summary
                                          ? processSummary(
                                              rec.noteType,
                                              rec.summary
                                            )
                                          : "No summary found."
                                      }
                                      alwaysFocus={false}
                                      onSaveContent={(newContent) => {
                                        updateContentOnFirebase(
                                          rec.id,
                                          "",
                                          newContent,
                                          "summary"
                                        );
                                      }}
                                    />
                                  </div>
                                )}
                              </div>

                              <div
                                style={{
                                  marginTop: "1rem",
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                <button
                                  className="button-copy"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: loadingRegenerateSummary
                                      ? "not-allowed"
                                      : "pointer",
                                  }}
                                  onClick={() =>
                                    handleOpenRegenerateDialog(
                                      rec.id,
                                      rec.summary,
                                      rec.timestamp,
                                      index
                                    )
                                  }
                                  disabled={loadingRegenerateSummary}
                                >
                                  {loadingRegenerateSummary ? (
                                    <img
                                      src={loadingSpinner}
                                      alt="Loading"
                                      style={{
                                        width: "1.5rem",
                                        marginRight: "0.8rem",
                                      }}
                                    />
                                  ) : (
                                    <AutoFixHighIcon sx={{ mr: 1 }} />
                                  )}
                                  Regenerate
                                </button>
                              </div>
                            </div>
                          )}
                          {activeTabs[rec.id] === 1 && (
                            // <p
                            //   dangerouslySetInnerHTML={{ __html: rec.transcription }}
                            // ></p>
                            <div style={{ width: "100%" }}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <button
                                  className="button-copy"
                                  onClick={() =>
                                    activeTabs[rec.id] === 0
                                      ? handleCopyToClipboard(
                                          rec.summaryVersions &&
                                            rec.summaryVersions.length > 0 &&
                                            rec.summaryVersionsCurrentIndex !==
                                              undefined
                                            ? processSummary(
                                                rec.summaryVersions[
                                                  rec
                                                    .summaryVersionsCurrentIndex
                                                ].noteType || "",
                                                rec.summaryVersions[
                                                  rec
                                                    .summaryVersionsCurrentIndex
                                                ].summary
                                              )
                                            : processSummary(
                                                rec.noteType,
                                                rec.summary
                                              )
                                        )
                                      : activeTabs[rec.id] === 1
                                      ? handleCopyToClipboard(
                                          rec.updatedTranscript &&
                                            rec.updatedTranscript.trim()
                                            ? rec.updatedTranscript
                                            : rec.transcription
                                        )
                                      : activeTabs[rec.id] === 2
                                      ? handleCopyToClipboard(
                                          rec.sessionNotes
                                            ? rec.sessionNotes
                                            : "No notes saved."
                                        )
                                      : activeTabs[rec.id] === 3
                                      ? handleCopyToClipboard(
                                          rec.clippeOutput
                                            ? rec.clippeOutput
                                            : ""
                                        )
                                      : null
                                  }
                                >
                                  {copied ? "Copied" : "Copy"}
                                </button>
                              </div>
                              <TipTap
                                content={processSummary(
                                  null,
                                  rec.updatedTranscript &&
                                    rec.updatedTranscript.trim()
                                    ? rec.updatedTranscript
                                    : rec.transcription
                                )}
                                alwaysFocus={false}
                                onSaveContent={(newContent) => {
                                  updateContentOnFirebase(
                                    rec.id,
                                    "",
                                    newContent,
                                    "transcription"
                                  );
                                }}
                              />
                            </div>
                          )}
                          {activeTabs[rec.id] === 2 && (
                            <div style={{ width: "100%" }}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <button
                                  className="button-copy"
                                  onClick={() =>
                                    activeTabs[rec.id] === 0
                                      ? handleCopyToClipboard(
                                          rec.summaryVersions &&
                                            rec.summaryVersions.length > 0 &&
                                            rec.summaryVersionsCurrentIndex !==
                                              undefined
                                            ? processSummary(
                                                rec.summaryVersions[
                                                  rec
                                                    .summaryVersionsCurrentIndex
                                                ].noteType || "",
                                                rec.summaryVersions[
                                                  rec
                                                    .summaryVersionsCurrentIndex
                                                ].summary
                                              )
                                            : processSummary(
                                                rec.noteType,
                                                rec.summary
                                              )
                                        )
                                      : activeTabs[rec.id] === 1
                                      ? handleCopyToClipboard(rec.transcription)
                                      : activeTabs[rec.id] === 2
                                      ? handleCopyToClipboard(
                                          rec.sessionNotes
                                            ? rec.sessionNotes
                                            : "No notes saved."
                                        )
                                      : activeTabs[rec.id] === 3
                                      ? handleCopyToClipboard(
                                          rec.clippeOutput
                                            ? rec.clippeOutput
                                            : ""
                                        )
                                      : null
                                  }
                                >
                                  {copied ? "Copied" : "Copy"}
                                </button>
                              </div>
                              <TipTap
                                content={
                                  rec.sessionNotes
                                    ? rec.sessionNotes.replace(/\n/g, "<br />")
                                    : "No notes saved."
                                }
                                alwaysFocus={false}
                                onSaveContent={(newContent) => {
                                  updateContentOnFirebase(
                                    rec.id,
                                    "",
                                    newContent,
                                    "sessionNotes"
                                  );
                                }}
                              />
                            </div>
                          )}
                          {activeTabs[rec.id] === 3 && (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                width: "100%",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              {rec.clippeOutput && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                >
                                  <button
                                    className="button-copy"
                                    style={{
                                      marginBottom: "1rem",
                                      alignSelf: "flex-end",
                                    }}
                                    onClick={() =>
                                      activeTabs[rec.id] === 0
                                        ? handleCopyToClipboard(
                                            rec.summaryVersions &&
                                              rec.summaryVersions.length > 0 &&
                                              rec.summaryVersionsCurrentIndex !==
                                                undefined
                                              ? processSummary(
                                                  rec.summaryVersions[
                                                    rec
                                                      .summaryVersionsCurrentIndex
                                                  ].noteType || "",
                                                  rec.summaryVersions[
                                                    rec
                                                      .summaryVersionsCurrentIndex
                                                  ].summary
                                                )
                                              : processSummary(
                                                  rec.noteType,
                                                  rec.summary
                                                )
                                          )
                                        : activeTabs[rec.id] === 1
                                        ? handleCopyToClipboard(
                                            rec.transcription
                                          )
                                        : activeTabs[rec.id] === 2
                                        ? handleCopyToClipboard(
                                            rec.sessionNotes
                                              ? rec.sessionNotes
                                              : "No notes saved."
                                          )
                                        : activeTabs[rec.id] === 3
                                        ? handleCopyToClipboard(
                                            rec.clippeOutput
                                              ? rec.clippeOutput
                                              : ""
                                          )
                                        : null
                                    }
                                  >
                                    {copied ? "Copied" : "Copy"}
                                  </button>
                                  {rec.clippeOutput
                                    .split("\n")
                                    .map((line, index) => (
                                      <React.Fragment key={index}>
                                        {line}
                                        <br />
                                      </React.Fragment>
                                    ))}
                                </div>
                              )}
                              {!rec.clippeOutput && (
                                <button
                                  className="button-copy"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    alignSelf: "center",
                                    cursor: loadingAnalyseWithClippe
                                      ? "not-allowed"
                                      : "pointer",
                                  }}
                                  onClick={() =>
                                    handleAnalyseWithClippe(rec.id, rec.summary)
                                  }
                                  disabled={loadingAnalyseWithClippe}
                                >
                                  {loadingAnalyseWithClippe ? (
                                    <img
                                      src={loadingSpinner}
                                      alt="Loading"
                                      style={{
                                        width: "1.5rem",
                                        marginRight: "0.8rem",
                                      }}
                                    />
                                  ) : (
                                    <AttachmentIcon sx={{ mr: 1 }} />
                                  )}
                                  Analyse with CLIPPE
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <button
                            className="button-small-brand-red"
                            style={{ alignSelf: "center" }}
                            onClick={() => handleDeleteRecording(rec.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {openRegenerateDialog && renderRegenerateSummaryModal()}
      {renderAssignToPatientModal()}
      {renderPushToMeddbaseModal()}
    </>
  );
};

export default HistoryTabContent;
